import {registerActorTarget} from '../game/actorTargetInput';
import {EnemyTargetLock} from '../game/actorTarget';
import {setHudContext} from '../game/hudContext';
import {renderResourceBar} from '../game/resourceBars';
import Phaser from 'phaser';
import {combatFeedback} from '../game/combatFeedback';
import {ghostDeath,spiritEffect} from '../game/spiritLifecycle';
import {angledRegion} from '../../../shared/regions.mjs';
import {createEndurance,requestSprint,requestDodge,stepEndurance} from '../../../shared/endurance.mjs';
import { CombatRuntime } from '../game/combatRuntime';
import { inHorizontalRange, MAX_LEVEL } from '../game/combat';
import {skillById, type Shortcut} from '../game/professions';
import {classSkillFx} from '../game/classSkillFx';
import {basicWeaponRange} from '../game/appearanceProfiles';
import {configureShortcutKeys,physicalKey} from '../input/keyboard';
import { gameActionBus, type GameAction } from '../input/actions';
import {createClanHouse} from '../game/clanHouseVisual';
import { ENEMIES, enemyDefinition, enemyFacingScale, enemyRenderScale, type EnemyDefinition, type EnemyId } from '../game/enemies';
import { CLEANSING_DELAY_MS, LocalProgressionLoop, expRequiredForLevel } from '../game/progression';
import {
  THAI_HUNT_ECONOMY,
  applyHuntItems,
  craftRecipe,
  createItemInventory,
  createWallet,
  huntReadiness,
  rollHuntReward,
  upgradeEquipment,
  itemName,
  type ItemInventoryState,
} from '../game/huntEconomy';
import { createGhostVisual, type GhostVisual } from '../game/ghostVisuals';
import {shortcutIcon} from '../game/hotbarPresentation';
import {hotbarState} from '../game/hotbarState';
import {updateWardUi} from '../game/skillStateUi';
import { Locomotion, WALKWAYS, LADDER, WORLD_WIDTH } from '../game/locomotion';
import { HunterRig } from '../game/hunterRig';
import { smooth } from '../game/motionPose';
import { GAME_MAPS, isSanctuary, type MapId } from '../game/worldMaps';
import { GEAR, allocate, rankUp, equip, unequip, changeProfession, createCharacter, reconcileCharacter, learnSkill, bindShortcut, setShortcutKeys, characterStats, loadCharacter, newCharacter, type CharacterState, type Appearance } from '../game/character';
import { derivedStats } from '../game/combat';
import { CharacterUi } from '../game/characterUi';
import { onlineSession, type OnlineEnemy, type OnlinePlayer, type OnlineState, type OnlineGroundDrop } from '../net/online';
import { projectOnlineWorld } from '../net/onlineWorld';
import { RemoteActor } from '../net/remoteActor';
import {layoutActorLabels,type ActorLabel,type LabelRect} from '../game/actorLabelLayout';
import {actorRoleCue,playerActorRelation,type ActorRoleCue} from '../game/actorRoleCue';
import {drawActorRoleCue} from '../game/actorRoleGraphics';
import {fitActorLabelText} from '../game/actorLabelText';
import {getPreferences} from '../game/preferences';
import { combatAudio } from '../game/combatAudio';
import {worldMusic} from '../game/worldMusic';
import {LANDMARKS} from '../game/worldPresentation';
import {createWorldNpcs} from '../game/worldNpcActors';
import {requestGhostCapture} from '../game/ghostCapture';
import {installGhostCaptureUi} from '../game/ghostCaptureUi';
import { ghostContentId } from '../game/thaiCombat';
import { nearestPickupDrop } from '../game/groundPickup';

export class BanBangPhiScene extends Phaser.Scene {
  private deathDeadline=0;
  private deathPanel:HTMLDivElement|undefined;
  private seenGhostDeaths=new Set<string>();
  private seenGhostBirths=new Set<string>();
  private player: Phaser.GameObjects.Container | undefined;
  private enemy: Phaser.GameObjects.Container | undefined;
  private actionLabel: Phaser.GameObjects.Text | undefined;
  private comboLabel: Phaser.GameObjects.Text | undefined;
  private cooldownLabel: Phaser.GameObjects.Text | undefined;
  private enemyLabel: Phaser.GameObjects.Text | undefined;
  private playerHpBar: Phaser.GameObjects.Graphics | undefined;
  private enemyHpBar: Phaser.GameObjects.Graphics | undefined;
  private telegraph: Phaser.GameObjects.Graphics | undefined;
  private mapId: MapId = 'village';
  private character = newCharacter();
  private characterUi?: CharacterUi;
  private portalHint?: Phaser.GameObjects.Text;
  private worldWidth = 4800;
  private warpSpawn: number | undefined;
  private warpPending=false;
  private localTalismans=3;
  private localWarpUntil=0;
  private warpRequestedAt=0;
  private heardInventory:Record<string,number>|null=null;
  private heardTradeIds=new Set<string>();
  private minimap?: HTMLCanvasElement;
  private extraEnemies=new Map<string,GhostVisual>();
  private groundDropVisuals=new Map<string,Phaser.GameObjects.Container>();
  private clanHouseVisuals=new Map<string,Phaser.GameObjects.Container>();
  private clanHouseSignature='';
  private lastUserActivityAt=0;
  private idleAutoRequested=false;
  private visibilityHandler?:()=>void;
  private lastServerError:string|null=null;
  private get groundRatio():number {return this.scale.height<560?Math.min(.65,(this.scale.height-145)/this.scale.height):.84;}
  private get worldScale():number {return this.scale.width / WORLD_WIDTH;}
  private moveAxis = 0;
  private endurance=createEndurance();
  private sprintHeld=false;
  private sprintNetworkRequested=false;
  private moveDepth = 0;
  private motion = new Locomotion();
  private enemyMotion = new Locomotion();
  private hunterRig: HunterRig | undefined;
  private lastSkillAction:number|undefined;
  private groundShadow: Phaser.GameObjects.Ellipse | undefined;
  private stepBeat = 0;
  private lastTrailAt = 0;
  private renderAltitude = 0;
  private supportHeight = 0;
  private facing: -1 | 1 = 1;
  private playerBaseY = 0;
  private playerNameplate?:Phaser.GameObjects.Text;
  private unsubscribeAction: (() => void) | undefined;
  private actionTween: Phaser.Tweens.Tween | undefined;
  private telegraphTimer: Phaser.Time.TimerEvent | undefined;
  private resetTimer: Phaser.Time.TimerEvent | undefined;
  private combat = new CombatRuntime();
  private progressionLoop = new LocalProgressionLoop();
  private huntInventory: ItemInventoryState = createItemInventory();
  private lastLootText = '';
  private forceLoot = false;
  private holdLootQa = false;
  private activeEnemyDef: EnemyDefinition = ENEMIES.pop;
  private normalEnemyIndex = 0;
  private enemyVisual: GhostVisual | undefined;
  private enemyBaseY = 0;
  private enemyDisplayScale = 1;
  private enemyTelegraphing = false;
  private unsubscribeOnline: (() => void) | undefined;
  private remotePlayers = new Map<string, RemoteActor>();
  private playerRoleMark:Phaser.GameObjects.Graphics|undefined;
  private enemyRoleMarks=new Map<string,Phaser.GameObjects.Graphics>();
  private targetLock=new EnemyTargetLock();
  private enemyTargetZones=new Map<string,Phaser.GameObjects.Zone>();
  private selectedWorldActor='';
  private worldLabelOffsets=new Map<string,{dx:number;dy:number}>();
  private worldHudRects:DOMRect[]=[];
  private worldHudCheck=0;
  private onlineEnemySnapshot: OnlineEnemy | null = null;
  private onlineEnemyId: string | null = null;
  private lastCombatFeedback='';
  private onlineEnabled = false;
  private get waitingAuthority():boolean{const s=onlineSession.current;return s.playerId!==null&&(!this.onlineEnabled||!s.snapshot); }
  private lastOnlineMoveAt = 0;
  private lastOnlineSentX = Number.NaN;
  private lastOnlineSentDepth = Number.NaN;
  private lastOnlineSentHeight = Number.NaN;

  constructor() { super('ban-bang-phi'); }

  create(data: { resizing?: boolean; playerRatio?: number; enemyRatio?: number; warp?: boolean } = {}): void {
    if(angledRegion(onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId)?.mapId??'')){this.scene.start('town');return;}
    this.warpPending=false;
    if(!data.resizing){const hadTarget=this.onlineEnemyId!==null;this.targetLock=new EnemyTargetLock();let reselect=false;try{reselect=sessionStorage.getItem('phi-target-reselect:'+onlineSession.current.roomId)==='1';}catch{}if(hadTarget||data.warp||reselect)this.targetLock.clear();}this.enemyTargetZones.clear();delete document.body.dataset.nativeTargetKey;
    this.selectedWorldActor='';delete document.body.dataset.selectedActorName;delete document.body.dataset.selectedActorId;this.worldLabelOffsets.clear();this.worldHudRects=[];this.worldHudCheck=0;
    this.extraEnemies.clear();this.enemyRoleMarks.clear();
    this.groundDropVisuals.clear();
    this.clanHouseVisuals.clear();this.clanHouseSignature='';
    this.lastUserActivityAt=this.time.now;
    this.idleAutoRequested=false;
    if (!data.resizing) {
      this.lastOnlineMoveAt = 0;
      this.lastOnlineSentX = Number.NaN;
      this.lastOnlineSentDepth = Number.NaN;
      this.lastOnlineSentHeight = Number.NaN;
      this.onlineEnemyId = null;
    }
    document.body.classList.add('gameplay-active');setHudContext('combat');
    worldMusic.mount();
    const params = new URLSearchParams(window.location.search);
    const qaMode = params.get('qa') === 'game';
    if (!data.resizing) {
      const requestedMap=params.get('map');
      this.mapId=requestedMap && requestedMap in GAME_MAPS?requestedMap as MapId:'village';
      this.character=qaMode?newCharacter():loadCharacter();
    }
    this.worldWidth=GAME_MAPS[this.mapId].width;
    if (!data.resizing) {
      this.activeEnemyDef = enemyDefinition(params.get('enemy'));
      this.forceLoot = params.get('loot') === 'all';
      this.holdLootQa = qaMode && params.get('holdLoot') === '1';
    }
    const requestedHaunting = qaMode
      ? Phaser.Math.Clamp(Number.parseInt(params.get('haunting') ?? '0', 10) || 0, 0, 100)
      : 0;
    const requestedLevel = qaMode
      ? Phaser.Math.Clamp(Number.parseInt(params.get('level') ?? '1', 10) || 1, 1, MAX_LEVEL)
      : 1;
    const requestedExp = qaMode ? Math.max(0, Number.parseInt(params.get('exp') ?? '0', 10) || 0) : 0;
    const requestedBie = qaMode ? Math.max(0, Number.parseInt(params.get('bie') ?? '0', 10) || 0) : 0;
    const requestedSpirit = qaMode ? Math.max(0, Number.parseInt(params.get('spirit') ?? '0', 10) || 0) : 0;
    if (!data.resizing) this.progressionLoop = new LocalProgressionLoop({
      progression: { level: requestedLevel, exp: requestedExp, bie: requestedBie, spiritEssence: requestedSpirit },
      world: {
        haunting: this.activeEnemyDef.boss ? 100 : requestedHaunting,
        bossActive: this.activeEnemyDef.boss,
      },
    });
    if (qaMode && !data.resizing && params.get('seedCraft') === '1') {
      this.huntInventory = createItemInventory({
        stacks: {
          'krasue-appendix': 1,
          'krasue-spirit-thread': 2,
          'hunter-bow-weathered': 1,
        },
        equipment: {
          'hunter-bow-weathered': { level: 0, blessing: 0 },
        },
      });
    }
    document.body.classList.toggle('boss-active', this.activeEnemyDef.boss);
    if(!data.resizing && !qaMode && !onlineSession.connected)try{
      const saved=JSON.parse(localStorage.getItem('phi-offline-progress-v1')??'null');
      if(saved?.progression)this.progressionLoop=new LocalProgressionLoop({progression:saved.progression});
      if(saved?.inventory)this.huntInventory=createItemInventory(saved.inventory);
    }catch{/* Start fresh when storage cannot be read. */}
    if (!data.resizing) this.combat = new CombatRuntime({
      enemyMaxHp: this.activeEnemyDef.maxHp,
      enemyDamage: this.activeEnemyDef.damage,
      level: this.progressionLoop.progression.level,
    });
    if (qaMode && !data.resizing) {
      const requestedEnemyHp = Number.parseInt(params.get('enemyHp') ?? '', 10);
      if (Number.isFinite(requestedEnemyHp) && requestedEnemyHp > 0) {
        this.combat.enemyHp = Phaser.Math.Clamp(requestedEnemyHp, 1, this.combat.enemyMaxHp);
      }
    }
    document.body.dataset.enemyId = this.activeEnemyDef.id;
    if(data.warp) {this.activeEnemyDef=ENEMIES[GAME_MAPS[this.mapId].enemy];this.combat.configureEnemy({enemyMaxHp:this.activeEnemyDef.maxHp,enemyDamage:this.activeEnemyDef.damage});}
    this.drawWorld();
    if (params.get('gallery') === '1') {
      document.body.dataset.gallery = '1';
      this.createGhostGallery();
      this.installResizeHandler();
      return;
    }
    delete document.body.dataset.gallery;
    this.createPlayer(!data.resizing || !!data.warp);
    this.createEnemy(!!data.resizing && !data.warp);
    if (data.resizing && this.player && this.enemy) {
      this.player.x = this.motion.x*this.worldScale;
      this.enemy.x = Phaser.Math.Clamp((data.enemyRatio ?? .65) * this.scale.width, 72, this.worldWidth*this.worldScale - 72);
      this.enemy.setVisible(this.combat.enemyHp > 0);
    }
    this.moveAxis = 0;
    this.cameras.main.scrollX=Phaser.Math.Clamp(this.motion.x*this.worldScale-this.scale.width*.44,0,this.worldWidth*this.worldScale-this.scale.width);
    this.moveDepth = 0;
    this.enemyTelegraphing = false;
    this.createHud();
    this.installHuntPrepUi(params.get('prep') === '1');
    if (qaMode && params.get('autoCraft') === '1') this.craftKrasueSpiritArrows();
    this.updateHud();
    this.installOnlineState();
    this.installCharacterUi();
    installGhostCaptureUi(this,this.worldScale,this.scale.height*this.groundRatio,text=>this.setActionText(text));
    this.applyCharacter();
    if(data.warp){this.setActionText('ถึง '+GAME_MAPS[this.mapId].name+' แล้ว');combatAudio.play('warp');}
    if(isSanctuary(this.mapId))this.setActionText('เขตปลอดผี · แตะ NPC เพื่อคุย');
    const key=(event:KeyboardEvent)=>{
      if(event.ctrlKey||event.metaKey||event.altKey)return;
      if(event.target instanceof Element&&event.target.closest('input,textarea,select,[contenteditable="true"]'))return;
      const panel=({i:'inventory',k:'skills',u:'equipment',p:'status'} as Record<string,string>)[physicalKey(event)];
      if(panel){event.preventDefault();this.characterUi?.show(this.characterUi.open?'':panel);}
      if(event.key==='Escape')this.characterUi?.show('');
    };
    window.addEventListener('keydown',key);
    this.visibilityHandler=()=>{if(this.onlineEnabled&&document.hidden)onlineSession.command('autoplay','on');};
    document.addEventListener('visibilitychange',this.visibilityHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN,()=>{window.removeEventListener('keydown',key);this.characterUi?.destroy();});
    this.unsubscribeAction = gameActionBus.subscribe((action) => this.handleAction(action));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.body.classList.remove('gameplay-active');
      document.body.classList.remove('boss-active');
      this.unsubscribeAction?.();
      this.unsubscribeOnline?.();
      this.clearRemotePlayers();
      for(const visual of this.groundDropVisuals.values())visual.destroy(true);this.groundDropVisuals.clear();
      if(this.visibilityHandler)document.removeEventListener('visibilitychange',this.visibilityHandler);
      this.telegraphTimer?.remove(false);
      this.resetTimer?.remove(false);
      this.deathPanel?.remove();this.deathPanel=undefined;
    });
    this.installResizeHandler();
    if (!isSanctuary(this.mapId) && this.combat.enemyHp <= 0) {
      const world = this.progressionLoop.world;
      const delay = world.cleansingUntilMs === null ? 1200 : Math.max(1, world.cleansingUntilMs - this.time.now);
      this.resetTimer = this.time.delayedCall(delay, () => {
        this.progressionLoop.tick(this.time.now);
        this.activateEnemy(this.progressionLoop.world.bossActive ? 'mae-tanee' : this.nextNormalEnemy());
      });
    } else if (this.combat.playerHp <= 0) {
      this.handlePlayerKo();
    } else if(!isSanctuary(this.mapId)) this.telegraphTimer = this.time.delayedCall(1400, () => this.startTelegraph());
    if(qaMode&&!data.resizing&&params.get('nineLives')==='1')this.huntInventory.stacks['nine-lives']=1;
    if(qaMode&&!data.resizing&&params.get('qaDeath')==='1')this.time.delayedCall(700,()=>{this.combat.playerHp=0;this.handlePlayerKo();this.updateHud();});
    if (qaMode && !data.resizing && params.get('qaKill') === '1') {
      this.time.delayedCall(240, () => {
        if (!this.enemy || this.combat.enemyHp <= 0) return;
        this.combat.enemyHp = 0;
        this.afterEnemyDamage();
      });
    }
  }

  private installResizeHandler(): void {
    const resize = (size: Phaser.Structs.Size, _base: Phaser.Structs.Size, _display: Phaser.Structs.Size, previousWidth: number) => {
      const oldWidth = previousWidth || size.width;
      this.scene.restart({ resizing: true, playerRatio: (this.player?.x ?? oldWidth * .38) / oldWidth, enemyRatio: (this.enemy?.x ?? oldWidth * .65) / oldWidth });
    };
    this.scale.on(Phaser.Scale.Events.RESIZE, resize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, resize));
  }

  update(time: number, delta: number): void {
    if(!this.onlineEnabled&&!this.waitingAuthority&&this.combat.playerHp>0)this.combat.mana=Math.min(100,this.combat.mana+Math.min(.5,delta/1000)*8);
    if(this.onlineEnabled&&!document.hidden&&!this.idleAutoRequested&&time-this.lastUserActivityAt>=30000){onlineSession.command('autoplay','on');this.idleAutoRequested=true;this.setActionText('AUTO PLAY · ไม่มีการควบคุม 30 วินาที');}
    if (!this.player) return;
    this.updateDeathState();
    if(this.warpPending && time-this.warpRequestedAt>4000){this.warpPending=false;this.setActionText('วาร์ปยังไม่สำเร็จ • ลองใหม่ได้');}
    const wasGrounded=this.motion.grounded;
    const canMove=this.combat.playerHp>0&&!this.characterUi?.open&&!this.warpPending;
    const wantsSprint=this.sprintHeld&&canMove&&this.motion.grounded&&!this.motion.climbing&&!this.motion.dodging&&Math.hypot(this.moveAxis,this.moveDepth)>.05&&this.time.now>=this.endurance.sprintReadyAt&&(this.endurance.sprinting?this.endurance.stamina>0:this.endurance.stamina>=12);
    if(this.onlineEnabled){if(wantsSprint!==this.sprintNetworkRequested){onlineSession.command('sprint',wantsSprint?'on':'off');this.sprintNetworkRequested=wantsSprint;}}
    else {requestSprint(this.endurance,wantsSprint,this.time.now,canMove);stepEndurance(this.endurance,this.time.now,delta/1000,this.combat.playerHp>0);}
    this.motion.step(delta / 1000, { axis: canMove ? this.moveAxis : 0, depth: canMove ? this.moveDepth : 0, sprint: wantsSprint&&this.endurance.sprinting });
    document.body.dataset.stamina=String(this.endurance.stamina);
    document.body.dataset.sprinting=String(this.endurance.sprinting);
    if(!wasGrounded&&this.motion.grounded)combatAudio.play('land',Math.min(1,.35+this.motion.landingSpeed/650));
    if(this.motion.mode==='walk'&&this.motion.grounded&&!this.characterUi?.open)combatAudio.play('step',.65);
    if(this.motion.mode==='climb')combatAudio.play('climb',.7);
    if(!this.onlineEnabled && this.combat.enemyHp>0 && !this.motion.dodging && Math.abs(this.motion.height-this.enemyMotion.height)<30 && Math.abs(this.motion.depth-this.enemyMotion.depth)<16){
      const enemyX=(this.enemy?.x??0)/this.worldScale,dx=this.motion.x-enemyX;
      if(Math.abs(dx)<72){this.motion.x=Phaser.Math.Clamp(enemyX+(dx<0?-72:72),65,this.worldWidth-65);this.motion.vx=0;}
    }
    this.facing = this.motion.facing;
    const camera=this.cameras.main;
    const target=Phaser.Math.Clamp(this.motion.x*this.worldScale-this.scale.width*.44,0,this.worldWidth*this.worldScale-this.scale.width);
    camera.scrollX=smooth(camera.scrollX,target,7,delta/1000);
    const portal=GAME_MAPS[this.mapId].portals.find(p=>Math.abs(p.x-this.motion.x)<85);
    this.portalHint?.setText(portal?'W / วาร์ป → '+portal.label:'').setVisible(!!portal);
    if(canMove && portal && this.moveDepth<-.1 && this.motion.height<=35 && !this.characterUi?.open ){this.warp();return;}
    const floor = this.scale.height * this.groundRatio + this.motion.depth * this.scale.height / 720;
    this.renderAltitude = this.motion.grounded ? smooth(this.renderAltitude,this.motion.height,24,delta/1000) : this.motion.height;
    if (this.motion.grounded) this.supportHeight = this.motion.height;
    const airGap=Math.max(0,this.motion.height-this.supportHeight);
    this.player.setPosition(this.motion.x * this.worldScale, floor - this.renderAltitude * this.scale.height / 720);
    this.player.setDepth(10 + this.motion.depth / 100);
    this.groundShadow?.setPosition(this.player.x, floor-this.supportHeight*this.scale.height/720).setAlpha(Math.max(.08, .42 - airGap / 300)).setScale(Math.max(.45, 1 - airGap / 300), 1);
    const actorScale = (this.scale.height < 560 ? Math.max(.65,this.scale.height/500) : 1.35) * (1 + this.motion.depth / 600);
    this.hunterRig?.setDead(this.combat.playerHp<=0);
    this.hunterRig?.keepsake(onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId)?.journey?.keepsake);
    this.hunterRig?.setPose(onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId)?.pose??'stand');
    this.hunterRig?.update(this.motion, delta / 1000, time, actorScale, this.player.x,Math.max(0,this.renderAltitude-this.supportHeight)*this.scale.height/720);
    this.playerNameplate?.setPosition(this.player.x,this.player.y-132*actorScale).setVisible(this.combat.playerHp>0);
    document.body.dataset.playerPose=this.hunterRig?.container.getData('idle-pose')??'active';
    document.body.dataset.playerFrame=String(this.hunterRig?.container.getData('motion-frame')??'');
    if (this.motion.landingSpeed > 160) {
      this.motionDust(this.player.x, this.player.y, 8);
      this.cameras.main.shake(75,.0012);
    }
    const beat = Math.floor(this.motion.gait / Math.PI);
    if (beat !== this.stepBeat && this.motion.grounded && Math.abs(this.motion.vx) > 50) this.motionDust(this.player.x,this.player.y,3);
    this.stepBeat = beat;
    if (this.motion.dodging && time - this.lastTrailAt > 45) {
      this.lastTrailAt = time;
      const trail=this.add.image(this.player.x,this.player.y-42,'gen-player-hunter').setDisplaySize(50,90).setFlipX(this.facing<0).setTint(0x99c9c0).setAlpha(.18).setDepth(8);
      this.tweens.add({targets:trail,alpha:0,duration:200,onComplete:()=>trail.destroy()});
    }
    if (this.onlineEnabled) { this.publishOnlineMove(time); this.updateOnlineActors(time,delta/1000); }
    else this.updateEnemyMovement(time, delta);
    if(!this.onlineEnabled)for(const visual of this.extraEnemies.values())visual.animate?.(delta/1000,time,0,false);
    this.syncEnemyHudPosition();
    this.arrangeWorldLabels();
    this.updateCooldowns(time);
    this.syncQaState();
  }

  private drawWorld(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#0b111a');
    const sky = this.add.graphics().setDepth(-40);
    sky.fillGradientStyle(0x101924, 0x101924, 0x26332f, 0x172324, 1);
    sky.fillRect(0, 0, width, height).setScrollFactor(0);
    const moonX = width * .64, moonY = height * .16;
    this.add.circle(moonX, moonY, height * .055, 0xd6ded5, .12).setDepth(-32).setScrollFactor(0);
    this.add.circle(moonX, moonY, height * .037, 0xc1ccbd, .72).setDepth(-31).setScrollFactor(0);
    this.cameras.main.setBounds(0,0,this.worldWidth*this.worldScale,height);
    for(let offset=0;offset<this.worldWidth/WORLD_WIDTH;offset++){
      this.add.image(width*(offset+.5),0,(this.mapId==='village'||isSanctuary(this.mapId))?'env-village-midground':'env-generated-village-far').setOrigin(.5,0).setDisplaySize(width+2,height*(this.groundRatio-.04)).setFlipX(offset%2===1).setTint(GAME_MAPS[this.mapId].tint).setDepth(-20);
      this.add.image(width*(offset+.5),height*(this.groundRatio-.04),'env-village-terrain').setOrigin(.5,0).setDisplaySize(width+2,height*(1-this.groundRatio+.04)).setFlipX(offset%2===1).setTint(GAME_MAPS[this.mapId].tint).setDepth(0);
    }
    if(this.mapId!=='village')for(const landmark of LANDMARKS[this.mapId]){
      this.add.image(landmark.x*this.worldScale,height*this.groundRatio,landmark.key).setOrigin(.5,1).setDisplaySize(landmark.width*this.worldScale,landmark.height*height/720).setTint(GAME_MAPS[this.mapId].tint).setDepth(-8);
    }
    for(const portal of GAME_MAPS[this.mapId].portals){
      const x=portal.x*this.worldScale,y=height*this.groundRatio;
      const sign=this.add.graphics().setDepth(6);
      sign.fillStyle(0x473020).fillRect(x-5,y-72,10,72);
      sign.fillStyle(0x78532e).fillRoundedRect(x-62,y-87,124,38,4);
      sign.lineStyle(2,0xb18c55).strokeRoundedRect(x-62,y-87,124,38,4);
      this.add.text(x,y-82,'→ '+portal.label+'\nW / ↑ / ปุ่มวาร์ป',{fontFamily:'Noto Sans Thai',fontSize:'11px',color:'#fff0c7',align:'center',stroke:'#352214',strokeThickness:2}).setOrigin(.5,0).setDepth(7);
    }
    this.portalHint=this.add.text(width/2,height*.35,'',{fontFamily:'Noto Sans Thai',fontSize:height<560?'12px':'16px',color:'#fff2cb',backgroundColor:'#10201ee6',padding:{x:12,y:8}}).setOrigin(.5).setScrollFactor(0).setDepth(35);
    document.body.dataset.enemyPresent=this.enemy?.active && this.enemy.visible?'1':'0';
    document.body.dataset.mapId=this.mapId;
    const zone=document.querySelector('.hud-zone-title strong');if(zone)zone.textContent=GAME_MAPS[this.mapId].name;
    // Independent moving atmosphere and lantern feedback retain a live layered world.
    for (const [x, y, radius] of [[.12,.38,.04],[.28,.54,.035],[.53,.60,.025]] as const) {
      const light = this.add.circle(width*x,height*y,height*radius,0xffa347,.055).setDepth(2).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({targets:light,alpha:.11,duration:1700+x*1200,yoyo:true,repeat:-1});
    }
    for (const [x, y] of [[.28,.69],[.73,.73]] as const) {
      const mist=this.add.ellipse(width*x,height*y,width*.55,height*.025,0xa8beb7,.065).setDepth(3);
      this.tweens.add({targets:mist,x:width*(x+.06),alpha:.025,duration:7000,yoyo:true,repeat:-1});
    }
    const edge=this.add.graphics().setDepth(5);
    edge.fillStyle(0x05080b,.28).fillRect(0,height*.96,width,height*.04);
    this.drawWalkways();
  }

  private drawWalkways(): void {
    if(isSanctuary(this.mapId))return;
    const { width, height }=this.scale;
    const sy=height/720, sx=width/WORLD_WIDTH;
    for (const surface of (this.worldWidth===WORLD_WIDTH?WALKWAYS:GAME_MAPS[this.mapId].surfaces)) {
      const y=height*this.groundRatio+(surface.depth-surface.height)*sy;
      const planks=this.add.image((surface.left+surface.right)/2*sx,y,'walkway-deck').setOrigin(.5,.06).setDisplaySize((surface.right-surface.left)*sx,Math.max(18,48*sy)).setDepth(4);
      planks.setTint(surface.id.startsWith('step')?0xb6a58c:0xc8bfa8);
      const rightY=height*this.groundRatio+(surface.depth-(surface.endHeight??surface.height))*sy;
      const g=this.add.graphics().setDepth(4.1);
      if(surface.endHeight!==undefined){
        planks.destroy();
        const shape=this.add.graphics();shape.fillStyle(0xffffff).fillPoints([{x:surface.left*sx,y},{x:surface.right*sx,y:rightY},{x:surface.right*sx,y:height*this.groundRatio},{x:surface.left*sx,y:height*this.groundRatio}],true);shape.setVisible(false);
        this.add.image((surface.left+surface.right)/2*sx,height*this.groundRatio,'env-village-terrain').setOrigin(.5,1).setDisplaySize((surface.right-surface.left)*sx,Math.max(surface.height,surface.endHeight)*sy+50).setMask(shape.createGeometryMask()).setDepth(4);
      }
      g.lineStyle(2,0x8d7142,.75).lineBetween(surface.left*sx,y,surface.right*sx,rightY);
    }
    const LADDER=this.worldWidth===WORLD_WIDTH?this.motion.world.ladder:GAME_MAPS[this.mapId].ladder;
    const ladderTop=height*this.groundRatio+(LADDER.depth-LADDER.height)*sy;
    this.add.image(LADDER.x*sx,ladderTop,'walkway-ladder').setOrigin(.5,0).setDisplaySize(Math.max(18,30*sx),LADDER.height*sy).setDepth(5);
    this.add.text(LADDER.x*sx,ladderTop-32,'W ปีน · S ลง',{fontFamily:'Noto Sans Thai',fontSize:'10px',color:'#d9c396',stroke:'#080c10',strokeThickness:3}).setOrigin(.5).setDepth(6);
  }


  private installCharacterUi():void {
    createWorldNpcs(this,this.mapId,this.worldScale,this.scale.height*this.groundRatio,id=>this.characterUi?.show('town',id));
    this.characterUi=new CharacterUi({state:()=>this.character,level:()=>this.combat.level,exp:()=>this.progressionLoop.progression.exp,hp:()=>this.combat.playerHp,loot:()=>this.onlineEnabled?{...onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId)?.inventory}:{...this.huntInventory.stacks,'warp-talisman':this.localTalismans},online:()=>this.onlineEnabled,act:(action,value)=>{
      if(this.waitingAuthority&&['potion','equip','unequip','bind'].includes(action)){this.characterUi?.log('รอข้อมูลยืนยันจากเซิร์ฟเวอร์ก่อนทำรายการ');return;}
      if(action==='warp'){this.warp();return;}
      if(action==='capture-use'){requestGhostCapture(text=>this.setActionText(text));this.characterUi?.show('');return;}
      if(action==='equip'||action==='unequip'||action==='potion'||action==='bind'||action==='learn'||action==='attribute')combatAudio.play(action==='potion'?'item':action==='equip'||action==='unequip'?'equip':'ui');
      if(action==='trade-confirm')combatAudio.play('trade');
      if(action==='warp-item'){
        if(this.warpPending||!Object.hasOwn(GAME_MAPS,value)||value===this.mapId||this.combat.playerHp<=0||this.motion.height>35){this.characterUi?.log('เลือกแผนที่อื่นและยืนบนพื้นก่อนใช้ยันต์');return;}
        if(this.onlineEnabled){onlineSession.command(action,value,()=>{this.warpPending=true;this.warpRequestedAt=this.time.now;});return;}
        if(this.localTalismans<1||this.time.now<this.localWarpUntil){this.characterUi?.log('ยันต์หมด หรือรอ 3 วินาทีก่อนใช้ซ้ำ');return;}
        this.localTalismans--;this.localWarpUntil=this.time.now+3000;this.mapId=value as MapId;this.warpSpawn=260;this.moveAxis=0;this.moveDepth=0;this.scene.restart({resizing:true,warp:true});return;
      }
      if(action==='buy-warp'&&!this.onlineEnabled){const p=this.progressionLoop.progression;if(this.mapId==='village'&&this.motion.x<360&&p.bie>=15&&this.combat.playerHp>0){p.bie-=15;this.localTalismans++;this.characterUi?.render(true);}else this.characterUi?.log('ซื้อที่เขตปลอดภัยในหมู่บ้าน • 15 เบี้ย');return;}
      if(this.onlineEnabled){onlineSession.command(action,value);return;}
      if(action==='buy-nine-lives'){if(this.combat.playerHp>0&&this.mapId==='town'&&this.progressionLoop.progression.bie>=1500){this.progressionLoop.progression.bie-=1500;this.huntInventory.stacks['nine-lives']=(this.huntInventory.stacks['nine-lives']??0)+1;this.updateProgressionHud();this.characterUi?.render(true);}return;}
      let changed=false;
      if(action==='create-character'){try{changed=createCharacter(this.character,JSON.parse(value) as Appearance);}catch{}}
      if(action==='npc-training'){if(this.mapId==='village'&&Math.abs(this.motion.x-190)<=110&&this.combat.level>=10&&this.character.created&&this.character.profession==='villager'){this.character.advancementQuest=true;changed=true;}else this.characterUi?.log('ต้องเป็นชาวบ้าน Lv.10 และยืนใกล้ครูฝึกในเมือง');}
      if(action==='class')changed=changeProfession(this.character,value,this.combat.level,this.character.advancementQuest===true);
      if(action==='learn')changed=learnSkill(this.character,this.combat.level,value);
      if(action==='bind'){try{const v=JSON.parse(value) as {slot:number;binding:Shortcut;source?:number};changed=bindShortcut(this.character,this.combat.level,v.slot,v.binding,v.source);}catch{}}
      if(action==='keys'){try{changed=setShortcutKeys(this.character,JSON.parse(value) as string[]);}catch{}}
      if(!changed&&['class','learn','bind','keys'].includes(action))this.setActionText('จัดช่องไม่ได้: ตรวจเลเวล อาชีพ หรือปุ่มที่ซ้ำ');
      if(action==='attribute' && ['power','vitality','guard'].includes(value))changed=allocate(this.character,this.combat.level,value as keyof CharacterState['attributes']);
      if(action==='rank')changed=rankUp(this.character,this.combat.level,Number(value));
      if(action==='equip')changed=equip(this.character,value);
      if(action==='unequip')changed=unequip(this.character,value);
      if(action==='potion' && this.character.potions>0 && this.combat.playerHp>0 && this.combat.playerHp<this.combat.playerMaxHp){this.character.potions--;this.combat.playerHp=Math.min(this.combat.playerMaxHp,this.combat.playerHp+40);changed=true;}
      if(changed){reconcileCharacter(this.character,this.combat.level);this.applyCharacter();this.updateHud();this.characterUi?.log(action==='unequip'?'ถอดเครื่องรางแล้ว':action==='equip'?'สวมใส่ '+(GEAR.find(g=>g.id===value)?.name??value):'ปรับปรุงตัวละครแล้ว');if(new URLSearchParams(location.search).get('qa')!=='game')try{localStorage.setItem('phi-character-v1',JSON.stringify(this.character));}catch{}}
    }});
    const characterQa=new URLSearchParams(location.search);
    if(characterQa.get('qa')==='game'&&characterQa.get('characterCreate')==='1')this.characterUi.show('identity');
    const ring=document.querySelector('.minimap-ring');if(ring){ring.innerHTML='<canvas id="world-minimap" width="320" height="140" aria-label="เส้นทาง ชั้นทางเดิน ตำแหน่งผู้เล่น และวาร์ป"></canvas>';this.minimap=ring.querySelector('canvas')!;const button=ring as HTMLElement;button.tabIndex=0;button.setAttribute('role','button');button.setAttribute('aria-label','เปิดแผนที่โลก');button.onclick=()=>this.characterUi?.show('worldmap');button.onkeydown=e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.stopPropagation();this.characterUi?.show('worldmap');}};}
  }
  private applyCharacter():void {
    reconcileCharacter(this.character,this.combat.level);
    configureShortcutKeys(this.character.shortcutKeys);
    {const base=derivedStats(this.combat.level),stats=characterStats(this.character,this.combat.level),old=this.combat.playerMaxHp;this.combat.bonusAtk=stats.atk-base.atk;this.combat.bonusHp=stats.maxHp-base.maxHp;this.combat.bonusDefense=stats.defense-base.defense;this.combat.skillRanks=[...this.character.ranks];this.combat.playerHp=Math.min(this.combat.playerMaxHp,this.combat.playerHp+Math.max(0,this.combat.playerMaxHp-old));}
    const outfitGear=GEAR.find(g=>g.id===this.character.equipment.outfit),weaponGear=GEAR.find(g=>g.id===this.character.equipment.weapon);
    this.hunterRig?.dress(outfitGear?.tint??0xffffff,weaponGear?.tint??0xffffff,weaponGear?.kind??'knife',outfitGear?.id??outfitGear?.kind??'villager',this.character.appearance.preset,this.character.appearance,this.character.profession,this.character.equipment.charm);
    const portrait=document.querySelector<HTMLElement>('.hud-portrait');if(portrait)portrait.style.backgroundImage=`url('/assets/characters/r15/${this.character.appearance.gender}/head.webp')`;
    const companionOwner=onlineSession.current.snapshot?.players.find(player=>player.id===onlineSession.current.playerId);
    this.hunterRig?.pet(companionOwner?.pet,companionOwner?.spiritSpecimen);
  }
  private warp():void {
    if(this.warpPending)return;
    if(this.combat.playerHp<=0){this.setActionText('ฟื้นตัวก่อนวาร์ป');return;}
    const portal=GAME_MAPS[this.mapId].portals.find(p=>Math.abs(p.x-this.motion.x)<85);
    if(!portal){this.characterUi?.log('เดินไปยังป้ายไม้บอกทางก่อน');return;}
    if(this.motion.height>35){this.setActionText('ลงมายืนบนพื้นก่อนวาร์ป');return;}
    // Enter the portal at road center, including authorities predating the full-road portal fix.
    if(this.onlineEnabled){this.warpPending=true;this.warpRequestedAt=this.time.now;this.moveAxis=0;this.moveDepth=0;this.motion.depth=0;this.motion.vz=0;onlineSession.sendMove(this.motion.x,0,this.motion.height,this.mapId);onlineSession.command('warp',portal.target);return;}
    const from=this.mapId;
    this.mapId=portal.target;this.moveAxis=0;this.moveDepth=0;
    const arrival=GAME_MAPS[this.mapId].portals.find(p=>p.target===from);
    this.warpSpawn=arrival && arrival.x>1000?GAME_MAPS[this.mapId].width-220:220;
    this.scene.restart({resizing:true,warp:true});
  }
  private drawMinimap():void {
    const ctx=this.minimap?.getContext('2d');if(!ctx)return;
    const width=320,height=140,map=GAME_MAPS[this.mapId],sx=300/this.worldWidth;
    ctx.clearRect(0,0,width,height);ctx.fillStyle='#101c1a';ctx.fillRect(0,0,width,height);
    const background=this.textures.get((this.mapId==='village'||isSanctuary(this.mapId))?'env-village-midground':'env-generated-village-far').getSourceImage();
    if(background instanceof HTMLImageElement||background instanceof HTMLCanvasElement)for(let i=0;i<Math.ceil(this.worldWidth/WORLD_WIDTH);i++)ctx.drawImage(background,10+i*WORLD_WIDTH*sx,5,WORLD_WIDTH*sx,105);
    ctx.fillStyle='#050e1480';ctx.fillRect(0,0,width,height);
    for(const landmark of LANDMARKS[this.mapId]){const image=this.textures.get(landmark.key).getSourceImage();if(image instanceof HTMLImageElement||image instanceof HTMLCanvasElement)ctx.drawImage(image,10+(landmark.x-landmark.width/2)*sx,110-landmark.height*.16,landmark.width*sx,landmark.height*.16);}
    document.body.dataset.minimapLandmarks=String(LANDMARKS[this.mapId].length);
    ctx.strokeStyle='#756f53';ctx.lineWidth=4;ctx.beginPath();ctx.moveTo(10,110);ctx.lineTo(310,110);ctx.stroke();
    for(const surface of this.motion.world.surfaces){ctx.strokeStyle=surface.endHeight!==undefined?'#90a97d':'#b39c69';ctx.lineWidth=3;ctx.beginPath();ctx.moveTo(10+surface.left*sx,110-surface.height*.5);ctx.lineTo(10+surface.right*sx,110-(surface.endHeight??surface.height)*.5);ctx.stroke();}
    ctx.fillStyle='#d6c9ed';for(const p of map.portals){ctx.fillRect(8+p.x*sx,99,5,16);}
    ctx.strokeStyle='#8cafaa';ctx.lineWidth=1;ctx.strokeRect(10+this.cameras.main.scrollX/this.worldScale*sx,6,WORLD_WIDTH*sx,121);
    ctx.fillStyle='#ffe19b';ctx.beginPath();ctx.arc(10+this.motion.x*sx,106-this.motion.height*.5+this.motion.depth*.15,4,0,Math.PI*2);ctx.fill();
    if(this.onlineEnabled){const snapshot=onlineSession.current.snapshot;for(const e of snapshot?.enemies??[]){if((e.mapId??'village')!==this.mapId||e.hp<=0)continue;ctx.fillStyle=e.boss?'#c696f0':'#df7770';ctx.beginPath();ctx.arc(10+e.x*sx,106,e.boss?4:3,0,Math.PI*2);ctx.fill();}for(const p of snapshot?.players??[]){if(p.id===onlineSession.current.playerId||(p.mapId??'village')!==this.mapId)continue;ctx.fillStyle='#7bd4d8';ctx.beginPath();ctx.arc(10+p.x*sx,106-(p.height??0)*.5+(p.depth??0)*.15,3,0,Math.PI*2);ctx.fill();}}
    else if(this.enemy?.visible){ctx.fillStyle='#df7770';ctx.beginPath();ctx.arc(10+this.enemy.x/this.worldScale*sx,106,3,0,Math.PI*2);ctx.fill();}
    ctx.font='10px system-ui';ctx.fillStyle='#efe2be';ctx.fillText('● คุณ   ● เพื่อน   ● ผี   ◈ วาร์ป',14,135);
    const caption=document.querySelector('.minimap-caption');if(caption)caption.textContent=map.name+' · '+Math.round(this.motion.x);
  }

  private motionDust(x:number,y:number,count:number): void {
    for(let i=0;i<count;i++){
      const mote=this.add.ellipse(x+(i-count/2)*3,y-2,4+i%3,3,0xc7b59a,.32).setDepth(12);
      this.tweens.add({targets:mote,x:mote.x+(i-count/2)*8,y:y-10-i%3*3,alpha:0,duration:260+i*15,onComplete:()=>mote.destroy()});
    }
  }

  private addCinematicForeground(groundY: number): void {
    const { width, height } = this.scale;
    const fg = this.add.graphics().setDepth(5);
    fg.fillStyle(0x030506, 0.92).fillRect(0, groundY + 34, width, Math.max(0, height - groundY - 34));
    fg.lineStyle(Math.max(9, height * 0.018), 0x050708, 0.96);
    fg.beginPath(); fg.moveTo(width * 0.07, groundY + 28); fg.lineTo(width * 0.11, height * 0.34); fg.lineTo(width * 0.18, height * 0.10); fg.strokePath();
    fg.lineStyle(Math.max(5, height * 0.009), 0x07090a, 0.94);
    fg.beginPath(); fg.moveTo(width * 0.14, height * 0.31); fg.lineTo(width * 0.30, height * 0.08); fg.strokePath();
    fg.beginPath(); fg.moveTo(width * 0.16, height * 0.27); fg.lineTo(width * 0.34, height * 0.20); fg.strokePath();
    for (const [x, y, rx, ry] of [[0.08, 0.29, 0.12, 0.16], [0.16, 0.18, 0.16, 0.13], [0.26, 0.12, 0.13, 0.10], [0.31, 0.21, 0.10, 0.09]] as const) {
      fg.fillStyle(0x050809, 0.88).fillEllipse(width * x, height * y, width * rx, height * ry);
    }
    fg.lineStyle(2, 0x050708, 0.96);
    for (let index = 0; index < 34; index += 1) {
      const x = (index / 33) * width; const blade = 12 + (index % 7) * 3;
      fg.beginPath(); fg.moveTo(x, groundY + 36); fg.lineTo(x + ((index % 3) - 1) * 5, groundY + 36 - blade); fg.strokePath();
    }
    for (const [x, y, w, a] of [[0.28, 0.69, 0.44, 0.045], [0.66, 0.64, 0.48, 0.035], [0.50, 0.75, 0.72, 0.028]] as const) {
      const mist = this.add.ellipse(width * x, height * y, width * w, Math.max(14, height * 0.045), 0xb7c2bf, a).setDepth(5.2);
      mist.setBlendMode(Phaser.BlendModes.SCREEN);
    }
    const warm = this.add.graphics().setDepth(5.1);
    for (const [x, y] of [[0.15, 0.57], [0.23, 0.66], [0.78, 0.64]] as const) {
      warm.fillStyle(0xd77e35, 0.08).fillCircle(width * x, height * y, Math.max(18, height * 0.05));
      warm.fillStyle(0xffc370, 0.72).fillCircle(width * x, height * y, 2.5);
    }
  }

  private addVillageHouse(x: number, groundY: number, key: string, targetHeight: number, tint: number): void {
    const house = this.add.image(x, groundY, key).setOrigin(0.5, 1).setTint(tint).setDepth(-8);
    const scale = Phaser.Math.Clamp(targetHeight / Math.max(1, house.height), 0.42, 1.15);
    house.setScale(scale);
    const warm = this.add.rectangle(x + house.displayWidth * 0.12, groundY - house.displayHeight * 0.34, Math.max(5, house.displayWidth * 0.07), Math.max(5, house.displayHeight * 0.08), 0xe8ad62, 0.55).setDepth(-7);
    warm.setStrokeStyle(1, 0x3c2922, 0.65);
  }

  private addBananaPlant(x: number, groundY: number, scale: number): void {
    const trunk = this.add.rectangle(0, -38, 9, 72, 0x786548, 1).setOrigin(0.5, 1);
    const leaves = [
      this.add.ellipse(-22, -82, 62, 15, 0x527353, 0.96).setAngle(-28),
      this.add.ellipse(20, -84, 66, 15, 0x5a7b57, 0.96).setAngle(26),
      this.add.ellipse(-6, -94, 58, 14, 0x66845d, 0.96).setAngle(-70),
      this.add.ellipse(8, -96, 58, 14, 0x607f5b, 0.96).setAngle(72),
      this.add.ellipse(0, -88, 72, 14, 0x6d8b61, 0.92),
    ];
    this.add.container(x, groundY, [trunk, ...leaves]).setScale(scale).setDepth(-2);
  }

  private addSpiritHouse(x: number, groundY: number, scale: number): void {
    const base = this.add.image(0, -34, 'env-shrine-base').setTint(0x9a7251).setScale(1.08);
    const pedestal = this.add.rectangle(0, -4, 36, 42, 0x6d4d36, 1).setOrigin(0.5, 1);
    const roof = this.add.triangle(0, -72, -38, 18, 38, 18, 0, -16, 0xb86b45, 1);
    const finial = this.add.rectangle(0, -96, 5, 24, 0xd1a76a, 1).setOrigin(0.5, 1);
    const lantern = this.add.circle(0, -50, 5, 0xffd27e, 0.86);
    const platform = this.add.rectangle(0, 0, 58, 9, 0x4b382c, 1).setOrigin(0.5, 1);
    this.add.container(x, groundY, [pedestal, base, roof, finial, lantern, platform]).setScale(scale).setDepth(-1);
  }

  private createPlayer(fresh = true): void {
    const { width, height } = this.scale;
    this.playerBaseY = height * this.groundRatio - 24;
    const compact = height < 560;
    this.groundShadow = this.add.ellipse(0,0,compact?38:48,compact?8:11,0x020407,.42).setDepth(7);
    this.hunterRig = new HunterRig(this);
    this.playerRoleMark=this.add.graphics().setDepth(7.01);
    const nameplate = this.add.text(0, compact ? -108 : -136, `ศิษย์อิสระ • Lv.${this.progressionLoop.progression.level}`, { fontFamily: 'Noto Sans Thai, system-ui', fontSize: `${Phaser.Math.Clamp(width * 0.011, 10, 14)}px`, color: '#fff0c9', stroke: '#05070a', strokeThickness: 5, shadow:{offsetX:0,offsetY:2,color:'#000000',blur:4,fill:true,stroke:true} }).setOrigin(0.5);
    this.playerNameplate=nameplate;
    const qaNear = new URLSearchParams(window.location.search).get('near') === '1';
    const initialX = qaNear ? width * 0.60 : width * 0.38;
    if (fresh) {
      const params=new URLSearchParams(window.location.search);
      const qa=params.get('qa')==='game';
      const spawnX=this.warpSpawn??(qa?Number(params.get('spawnX')??initialX/width*WORLD_WIDTH):initialX/width*WORLD_WIDTH);
      this.warpSpawn=undefined;
      this.motion = new Locomotion(Phaser.Math.Clamp(spawnX,65,this.worldWidth-65),{width:this.worldWidth,surfaces:this.worldWidth===WORLD_WIDTH?WALKWAYS:GAME_MAPS[this.mapId].surfaces,ladder:this.worldWidth===WORLD_WIDTH?LADDER:GAME_MAPS[this.mapId].ladder});
      this.motion.depth=qa?Phaser.Math.Clamp(Number(params.get('spawnDepth')??0),-70,36):0;
      this.motion.height=qa?Math.max(0,Number(params.get('spawnHeight')??0)):0;
      this.renderAltitude=this.motion.height;
      this.supportHeight=this.motion.height;
    }
    nameplate.setDepth(10000);
    this.player = this.add.container(initialX,this.playerBaseY,[this.hunterRig.container]).setDepth(10);
    this.player.setInteractive(new Phaser.Geom.Rectangle(-40,-170,80,180),Phaser.Geom.Rectangle.Contains);
    registerActorTarget(this,this.player,'player','local',()=>{const me=onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId);if(me)this.selectWorldIdentity(me);},()=>this.combat.playerHp>0,()=>({x:this.player!.x-40,y:this.player!.y-170,width:80,height:180}));
  }

  private createGhostGallery(): void {
    const { width, height } = this.scale;
    const y = height * this.groundRatio - 30;
    const ids = ['pop', 'krasue', 'tanee', 'mae-tanee'] as const;
    const xs = [0.16, 0.38, 0.61, 0.83];
    this.add.text(width / 2, height * 0.12, 'R05 • THAI GHOST SILHOUETTE QA', {
      fontFamily: 'system-ui', fontStyle: 'bold', fontSize: `${Phaser.Math.Clamp(width * 0.021, 16, 24)}px`,
      color: '#f4deb1', stroke: '#101623', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(30);
    ids.forEach((id, index) => {
      const def = ENEMIES[id];
      const visual = createGhostVisual(this, id);
      const galleryScale = id === 'mae-tanee' ? 0.88 : Math.min(0.9, def.scale * 0.82);
      visual.container.setPosition(width * (xs[index] ?? 0.5), y).setScale(galleryScale).setDepth(10);
      this.add.text(width * (xs[index] ?? 0.5), y + 72, `${def.boss ? 'BOSS • ' : ''}${def.nameTh}`, {
        fontFamily: 'system-ui', fontStyle: def.boss ? 'bold' : 'normal',
        fontSize: `${Phaser.Math.Clamp(width * 0.014, 12, 17)}px`,
        color: def.boss ? '#ffe39a' : '#f4e7cc', stroke: '#111827', strokeThickness: 4,
      }).setOrigin(0.5, 0).setDepth(30);
    });
  }

  private currentEnemy():Phaser.GameObjects.Container|undefined{return this.enemy;}

  private createEnemy(preserve=false): void {
    if(isSanctuary(this.mapId)){
      this.enemy=undefined;this.enemyLabel=undefined;this.enemyVisual=undefined;
      this.onlineEnemySnapshot=null;this.onlineEnemyId=null;
      this.combat.enemyHp=0;this.combat.enemyStunnedUntil=0;
      this.enemyTelegraphing=false;this.telegraph=undefined;
      return;
    }
    const { width, height } = this.scale;
    this.enemyBaseY = height * this.groundRatio - (this.activeEnemyDef.id==='pop'?0:28);
    this.enemyVisual = createGhostVisual(this, this.activeEnemyDef.id,this.onlineEnabled?this.onlineEnemySnapshot??undefined:undefined);
    this.enemyDisplayScale = enemyRenderScale(this.activeEnemyDef, height);
    this.enemy = this.enemyVisual.container
      .setPosition(this.motion.x>1000 && !this.onlineEnabled?Phaser.Math.Clamp((this.motion.x+300)*this.worldScale,72,this.worldWidth*this.worldScale-72):width*.67, this.enemyBaseY)
      .setScale(this.enemyDisplayScale)
      .setDepth(9);
    const qaParams=new URLSearchParams(location.search);
    if(qaParams.get('qa')==='game' && qaParams.has('enemyX'))this.enemy.x=Phaser.Math.Clamp(Number(qaParams.get('enemyX'))*this.worldScale,72,this.worldWidth*this.worldScale-72);
    if(!preserve)this.enemyMotion=new Locomotion(this.enemy.x/this.worldScale,this.motion.world);
    this.enemyLabel = this.add.text(this.enemy.x, this.enemyBaseY - (this.activeEnemyDef.boss ? 150 : 94), '', {
      fontFamily: 'system-ui',
      fontSize: `${Phaser.Math.Clamp(width * 0.014, 12, 17)}px`,
      fontStyle: this.activeEnemyDef.boss ? 'bold' : 'normal',
      color: this.activeEnemyDef.boss ? '#ffe3a6' : '#f0e2bf',
      stroke: '#101822',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(12);
  }

  private installOnlineState(): void {
    this.unsubscribeOnline?.();
    this.unsubscribeOnline = onlineSession.subscribe((state) => this.applyOnlineState(state));
  }

  private applyOnlineState(state: Readonly<OnlineState>): void {
    const wasOnline=this.onlineEnabled;
    this.onlineEnabled = state.status === 'online' && state.snapshot !== null;
    if(wasOnline&&!this.onlineEnabled){this.targetLock.clear();for(const zone of this.enemyTargetZones.values())zone.destroy();this.enemyTargetZones.clear();document.body.dataset.enemyTargetId='';document.body.dataset.enemyTargetBlocked='true';}
    if(!state.error)this.lastServerError=null;
    if(state.error)this.warpPending=false;
    if(state.error&&state.error!==this.lastServerError){this.warpPending=false;this.lastServerError=state.error;this.setActionText(state.error);}
    document.body.dataset.online = this.onlineEnabled ? '1' : '0';
    document.body.dataset.roomId = state.roomId ?? '';
    if (!this.onlineEnabled || !state.snapshot) {
      this.warpPending=false;
      this.onlineEnemyId = null;
      this.clearRemotePlayers();
      return;
    }

    this.telegraphTimer?.remove(false);
    this.telegraph?.destroy();
    this.telegraph = undefined;
    this.enemyTelegraphing = false;
    const levelBeforeSnapshot=this.combat.level;
    const view = projectOnlineWorld(state.snapshot, state.playerId, this.onlineEnemyId);
    view.activeEnemy=this.targetLock.resolve(state.snapshot.enemies,this.mapId,view.activeEnemy);
    this.syncEnemyTargets(state.snapshot.enemies);
    document.body.dataset.enemyTargetId=view.activeEnemy?.id??'';document.body.dataset.enemyTargetBlocked=String(this.targetLock.blocked);
    if (view.localPlayer) {
      this.setDomText('hud-player-name', view.localPlayer.name);
      this.playerNameplate?.setText(`${view.localPlayer.name} • Lv.${view.localPlayer.progression.level}`);
      this.progressionLoop = new LocalProgressionLoop({
        progression: view.localPlayer.progression,
        world: {
          haunting: state.snapshot.haunting,
          bossActive: state.snapshot.bossActive,
          cleansingUntilMs: state.snapshot.cleansingUntilMs,
          cycle: state.snapshot.cycle,
        },
      });
      this.combat.setLevel(view.localPlayer.progression.level);
      if (Number.isNaN(this.lastOnlineSentX)) {
        this.motion.x = view.localPlayer.x;
        this.motion.depth = view.localPlayer.depth ?? 0;
        this.motion.height = view.localPlayer.height ?? 0;
        this.lastOnlineSentX = view.localPlayer.x;
      } else {
        // Reconcile only an acknowledged input; stale snapshots cannot rewind jump/dodge.
        const correction = onlineSession.consumeMoveCorrection(view.localPlayer);
        if (correction) {
          this.motion.x = Phaser.Math.Clamp(this.motion.x + correction.x, 65, this.worldWidth - 65);
          this.motion.depth = Phaser.Math.Clamp(this.motion.depth + correction.depth, -70, 36);
          this.motion.height = Math.max(0, this.motion.height + correction.height);
        }
      }
    }
    const previousPlayerHp=this.combat.playerHp;
    if(this.combat.level>levelBeforeSnapshot)combatAudio.play('level');
    if(view.localPlayer&&previousPlayerHp<=0&&(view.localPlayer.hp??0)>0){this.motion.x=view.localPlayer.x;this.motion.depth=view.localPlayer.depth??0;this.motion.height=view.localPlayer.height??0;this.motion.vx=0;this.motion.vz=0;this.lastOnlineSentX=view.localPlayer.x;this.player?.setAlpha(1);this.hunterRig?.setDead(false);if(this.player)spiritEffect(this,this.player.x,this.player.y,true);combatAudio.play('respawn');}
    if(view.localPlayer?.inventory){const inv=view.localPlayer.inventory;if(this.heardInventory){if(Object.keys(inv).some(id=>(inv[id]??0)>(this.heardInventory![id]??0)))combatAudio.play('loot');if((inv.potion??0)<(this.heardInventory.potion??0))combatAudio.play('item');}this.heardInventory={...inv};}
    for(const trade of state.snapshot.trades??[])if(trade.status==='complete'&&trade.players.includes(state.playerId!)&&!this.heardTradeIds.has(trade.id)){this.heardTradeIds.add(trade.id);combatAudio.play('trade');}
    if(view.localPlayer?.profile){this.character=structuredClone(view.localPlayer.profile) as CharacterState;reconcileCharacter(this.character,view.localPlayer.progression.level);this.applyCharacter();if(view.localPlayer.hp!==undefined)this.combat.playerHp=view.localPlayer.hp;}
    if(this.combat.playerHp<previousPlayerHp){if(this.player)combatFeedback(this,this.player.x,this.playerFeedbackY(),previousPlayerHp-this.combat.playerHp,'received');this.hunterRig?.hit();combatAudio.play('hit');this.cameras.main.shake(70,.002);if(this.combat.playerHp<=0)this.handlePlayerKo();}
    if(view.localPlayer?.cooldowns){for(const [id,readyAt] of Object.entries(view.localPlayer.cooldowns))this.combat.classReadyAt[id]=this.time.now+Math.max(0,readyAt-Date.now());}
    if(view.localPlayer){
      this.combat.mana=view.localPlayer.mana??100;
      if(view.localPlayer.endurance){const e=view.localPlayer.endurance;this.endurance={...e,sprintReadyAt:this.time.now+Math.max(0,e.sprintReadyAt-Date.now()),dodgeReadyAt:this.time.now+Math.max(0,e.dodgeReadyAt-Date.now()),regenAt:this.time.now+Math.max(0,e.regenAt-Date.now())};this.combat.dodgeReadyAt=Math.max(this.combat.dodgeReadyAt,this.endurance.dodgeReadyAt);}
      const action=view.localPlayer.actionAt??0;
      if(this.lastSkillAction!==undefined&&action>this.lastSkillAction&&view.localPlayer.actionKind==='cast'&&view.localPlayer.actionSkill){
        classSkillFx(this,view.localPlayer.actionSkill,this.player!.x,this.player!.y,view.localPlayer.facing??1,this.enemy?.x);
        this.hunterRig?.attack();combatAudio.play('spell');this.setActionText(skillById(view.localPlayer.actionSkill)?.name??'ร่ายวิชา');
      }
      this.lastSkillAction=action;
    }
    if(this.combat.playerHp>0)this.updateDeathState();
    if(view.localPlayer?.mapId && view.localPlayer.mapId!==this.mapId && view.localPlayer.mapId in GAME_MAPS){this.mapId=view.localPlayer.mapId as MapId;this.warpSpawn=view.localPlayer.x;this.lastOnlineSentX=NaN;this.scene.restart({resizing:true,warp:true});return;}
    this.syncRemotePlayers(view.remotePlayers.filter(p=>(p.mapId??'village')===this.mapId));
    for(const dead of state.snapshot.ghostDeaths??[]){
      if((dead.mapId??'village')!==this.mapId||this.seenGhostDeaths.has(dead.id))continue;
      this.seenGhostDeaths.add(dead.id);
      if(dead.combatFeedback&&this.enemy&&dead.id===this.onlineEnemyId&&dead.combatFeedback.id!==this.lastCombatFeedback){const feedback=dead.combatFeedback;this.lastCombatFeedback=feedback.id;combatFeedback(this,this.enemy.x,this.enemyFeedbackY(),feedback.damage,feedback.kind);}
      const visual=createGhostVisual(this,dead.kind,dead);
      const scale=this.scale.height<560?Math.max(.65,this.scale.height/500):1.35;
      visual.container.setPosition(dead.x*this.worldScale,this.scale.height*this.groundRatio+(dead.activity?.depth??0)*this.scale.height/720).setScale(scale).setDepth(9);
      ghostDeath(this,visual.container,dead.kind);
    }
    const candidates=state.snapshot.enemies.filter(e=>(e.mapId??'village')===this.mapId&&e.hp>0).sort((a,b)=>Math.abs(a.x-this.motion.x)-Math.abs(b.x-this.motion.x));
    this.applyOnlineEnemy(candidates[0]??null);
    const extras=candidates.slice(1);const ids=new Set(extras.map(e=>e.id));
    for(const [id,v] of this.extraEnemies)if(!ids.has(id)){v.container.destroy(true);this.extraEnemies.delete(id);}
    for(const e of extras){let v=this.extraEnemies.get(e.id);if(!v){v=createGhostVisual(this,e.kind,e);this.extraEnemies.set(e.id,v);v.container.setPosition(e.x*this.worldScale,this.scale.height*this.groundRatio);if(!this.seenGhostBirths.has(e.id)){this.seenGhostBirths.add(e.id);spiritEffect(this,v.container.x,v.container.y,true);}}v.container.setData('snapshot',e).setDepth(9);}
    this.syncGroundDrops(state.snapshot.groundDrops??[]);
    const villages=(state.snapshot.villages??[]).filter(v=>v.house?.mapId===this.mapId);
    const houseSignature=JSON.stringify(villages);if(houseSignature!==this.clanHouseSignature){this.clanHouseSignature=houseSignature;for(const v of this.clanHouseVisuals.values())v.destroy(true);this.clanHouseVisuals.clear();for(const v of villages){const h=v.house!;this.clanHouseVisuals.set(v.id,createClanHouse(this,v.name,h.level,h.hp,h.maxHp).setPosition(h.x*this.worldScale,this.scale.height*this.groundRatio).setScale(this.worldScale));}}
    this.updateProgressionHud();
    this.characterUi?.render();
    document.body.dataset.onlinePlayers = String(state.snapshot.players.length);
    document.body.dataset.remotePlayers = String(view.remotePlayers.filter(p=>(p.mapId??'village')===this.mapId).length);
  }

  private syncGroundDrops(drops:OnlineGroundDrop[]):void {
    const visible=drops.filter(d=>d.mapId===this.mapId);
    const ids=new Set(visible.map(d=>d.id));
    for(const [id,visual] of this.groundDropVisuals)if(!ids.has(id)){visual.destroy(true);this.groundDropVisuals.delete(id);}
    for(const drop of visible){
      let visual=this.groundDropVisuals.get(drop.id);
      if(!visual){
        const glow=this.add.circle(0,0,15,0xe5b861,.18).setStrokeStyle(2,0xf6d88b,.75);
        glow.setInteractive(new Phaser.Geom.Circle(0,0,24),Phaser.Geom.Circle.Contains);
        const gem=this.add.text(0,-4,'◆',{fontFamily:'system-ui',fontSize:'20px',color:'#f6d88b',stroke:'#17120b',strokeThickness:3}).setOrigin(.5);
        const label=this.add.text(0,-28,itemName(drop.itemId)+' ×'+drop.quantity+'\n[G] เก็บ',{fontFamily:'Noto Sans Thai, system-ui',fontSize:'11px',align:'center',color:'#fff1c7',backgroundColor:'rgba(10,12,16,.72)',padding:{x:5,y:2}}).setOrigin(.5,1);
        visual=this.add.container(drop.x*this.worldScale,this.scale.height*this.groundRatio,[glow,gem,label]).setDepth(12);
        registerActorTarget(this,glow,'drop',drop.id,()=>onlineSession.command('pickup',drop.id),()=>!!onlineSession.current.snapshot?.groundDrops?.some(d=>d.id===drop.id&&d.mapId===this.mapId),()=>{const b=glow.getBounds();return{x:b.centerX-24,y:b.centerY-24,width:48,height:48};});
        this.groundDropVisuals.set(drop.id,visual);
      }
      visual.setPosition(drop.x*this.worldScale,this.scale.height*this.groundRatio-5+Math.sin(this.time.now/260+drop.x)*3);
    }
  }


  private pickupNearestGroundDrop():void {
    if(!this.onlineEnabled){this.setActionText('โหมดออฟไลน์รับของเข้ากระเป๋าอัตโนมัติ');return;}
    const state=onlineSession.current;
    const drop=nearestPickupDrop(state.snapshot?.groundDrops??[],{
      mapId:this.mapId,
      playerX:this.motion.x,
      playerId:state.playerId,
      nowMs:Date.now(),
    });
    if(!drop){this.setActionText('ไม่มีของที่เก็บได้ในระยะ');return;}
    this.lastUserActivityAt=this.time.now;
    if(this.idleAutoRequested&&!document.hidden){onlineSession.command('autoplay','off');this.idleAutoRequested=false;}
    onlineSession.command('pickup',drop.id);
    this.setActionText('กำลังเก็บ '+itemName(drop.itemId)+' ×'+drop.quantity);
  }

  private applyOnlineEnemy(enemy: OnlineEnemy | null): void {
    this.onlineEnemySnapshot=enemy;
    if (!enemy) {
      this.onlineEnemyId = null;
      this.enemy?.setVisible(false);
      this.enemyLabel?.setVisible(false);
      this.setDomText('hud-objective-enemy', 'กำลังชำระอาถรรพ์ • รอผีชุดใหม่');
      const bossPanel = document.getElementById('hud-boss');
      if (bossPanel) bossPanel.hidden = true;
      return;
    }
    const changedEnemy = this.onlineEnemyId !== enemy.id || this.activeEnemyDef.id !== enemy.kind;
    const previousHp = changedEnemy ? enemy.hp : this.combat.enemyHp;
    if (changedEnemy) {
      this.onlineEnemyId = enemy.id;
      this.activeEnemyDef = ENEMIES[enemy.kind];
      this.enemy?.destroy(true);
      this.enemyLabel?.destroy();
      this.enemy = undefined;
      this.enemyLabel = undefined;
      this.enemyVisual = undefined;
      this.combat.configureEnemy({ enemyMaxHp: enemy.maxHp, enemyDamage: this.activeEnemyDef.damage });
      this.createEnemy();
      if(!this.seenGhostBirths.has(enemy.id)){this.seenGhostBirths.add(enemy.id);this.enemyBirthEffect(enemy.x*this.worldScale);}
    }
    this.combat.enemyMaxHp = enemy.maxHp;
    this.combat.enemyHp = enemy.hp;
    if (this.enemy) {
      this.enemy.setVisible(enemy.hp > 0);
      this.enemy.setAlpha(enemy.hp > 0 ? 1 : .18);
      if(changedEnemy)this.enemy.x = enemy.x*this.worldScale;
      this.enemyMotion.x=enemy.x;this.enemyMotion.depth=enemy.activity?.depth??0;this.enemyMotion.height=0;
    }
    document.body.classList.toggle('boss-active', enemy.boss);
    const bossPanel = document.getElementById('hud-boss');
    if (bossPanel) bossPanel.hidden = !enemy.boss;
    if(changedEnemy)this.lastCombatFeedback=enemy.combatFeedback?.id??'';
    const feedback=enemy.combatFeedback;
    if(!changedEnemy&&feedback&&feedback.id!==this.lastCombatFeedback){
      this.lastCombatFeedback=feedback.id;
      if(this.enemy)combatFeedback(this,this.enemy.x,this.enemyFeedbackY(),feedback.damage,feedback.kind);
      if(feedback.damage>0)this.hitFx(feedback.kind==='critical'?0xffd789:0xf2b6a0);
    }else if (!changedEnemy && !feedback && enemy.hp < previousHp) {
      this.hitFx(enemy.boss ? 0xd989ff : 0xf2b6a0);
      this.showDamage(previousHp - enemy.hp, false);
    }
    this.updateHud();
  }

  private syncRemotePlayers(players: OnlinePlayer[]): void {
    const liveIds = new Set(players.map((player) => player.id));
    for (const [id, actor] of this.remotePlayers) {
      if (liveIds.has(id)) continue;
      actor.destroy();
      this.remotePlayers.delete(id);
      this.worldLabelOffsets.delete(id);
      if(this.selectedWorldActor===id){this.selectedWorldActor='';delete document.body.dataset.selectedActorName;delete document.body.dataset.selectedActorId;}
    }
    for (const remote of players) {
      let actor = this.remotePlayers.get(remote.id);
      if (!actor) { actor=new RemoteActor(this,remote,player=>this.selectWorldIdentity(player));this.remotePlayers.set(remote.id,actor); }
      else actor.receive(remote);
    }
  }

  private clearRemotePlayers(): void {
    for (const actor of this.remotePlayers.values()) actor.destroy();
    this.remotePlayers.clear();
    this.worldLabelOffsets.clear();
    this.selectedWorldActor='';delete document.body.dataset.selectedActorName;delete document.body.dataset.selectedActorId;
    document.body.dataset.remotePlayers = '0';
  }

  private updateOnlineActors(time:number,seconds:number):void {
    const scale=this.scale.height<560?Math.max(.65,this.scale.height/500):1.35;
    for(const actor of this.remotePlayers.values()){
      actor.update(seconds,time,this.worldScale,this.scale.height*this.groundRatio,scale);
      document.body.dataset.remotePose=String(actor.container.getData('pose'));
      document.body.dataset.remoteArm=String(actor.container.getData('armAngle'));
      document.body.dataset.remoteKnee=String(actor.container.getData('kneeAngle'));
      document.body.dataset.remoteAttacking=String(actor.container.getData('attacking'));
      document.body.dataset.remoteIdlePose=String(actor.rig.container.getData('idle-pose'));
    }
    const animate=(visual:GhostVisual,snapshot:OnlineEnemy)=>{
      visual.container.setData('snapshot',snapshot);const activity=snapshot.activity;
      visual.container.x=smooth(visual.container.x,snapshot.x*this.worldScale,14,seconds);
      const y=this.scale.height*this.groundRatio+(activity?.depth??0)*this.scale.height/720;
      visual.container.y=y+(snapshot.archetypeId?Math.sin(time/350)*2:snapshot.kind==='pop'?0:Math.sin(time/250)*5-26);
      visual.container.setScale(enemyFacingScale(scale,activity?.facing??-1),scale);
      const attacking=activity?.mode==='windup'||activity?.mode==='attack';
      visual.animate?.(seconds,time,activity?.vx??0,attacking);
      visual.container.setAlpha(activity?.mode==='windup'?.65+Math.sin(time/60)*.25:1);
      let ring=visual.container.getData('onlineTelegraph') as Phaser.GameObjects.Ellipse|undefined;
      if(!ring){ring=this.add.ellipse(0,0,110,22,0xff903b,.12).setStrokeStyle(3,0xffb550,.9);visual.container.addAt(ring,0);visual.container.setData('onlineTelegraph',ring);}
      ring.setVisible(activity?.mode==='windup');
      if(!visual.animate)visual.container.rotation=attacking?Math.sin(time/80)*.08:Math.sin(time/450)*.015;
    };
    if(this.enemyVisual&&this.onlineEnemySnapshot){animate(this.enemyVisual,this.onlineEnemySnapshot);document.body.dataset.ghostMode=this.onlineEnemySnapshot.activity?.mode??'idle';document.body.dataset.ghostX=String(this.onlineEnemySnapshot.x);}
    document.body.dataset.namedGhostNames=JSON.stringify([this.enemyVisual,...this.extraEnemies.values()].filter(v=>v?.container.visible&&v.container.getData('archetype-id')).map(v=>v!.container.getData('display-name')));
    for(const visual of this.extraEnemies.values()){const snapshot=visual.container.getData('snapshot') as OnlineEnemy|undefined;if(snapshot)animate(visual,snapshot);}
  }

  private publishOnlineMove(now: number): void {
    if(this.warpPending)return;
    if (!this.player || now - this.lastOnlineMoveAt < 80) return;
    const worldX = this.motion.x;
    if (Number.isFinite(this.lastOnlineSentX) && Math.abs(worldX-this.lastOnlineSentX)<1 && Math.abs(this.motion.depth-this.lastOnlineSentDepth)<1 && Math.abs(this.motion.height-this.lastOnlineSentHeight)<1 && this.motion.grounded) return;
    this.lastOnlineMoveAt = now;
    this.lastOnlineSentX = worldX;
    this.lastOnlineSentDepth = this.motion.depth;
    this.lastOnlineSentHeight = this.motion.height;
    onlineSession.sendMove(worldX,this.motion.depth,this.motion.height,this.mapId);
  }

  private sendOnlineAttack(action: 'basic' | 'skill-1' | 'skill-2' | 'skill-3', range: number): void {
    const live=onlineSession.current.snapshot?.enemies.find(e=>e.id===this.onlineEnemyId&&e.mapId===this.mapId&&e.hp>0);
    if (this.targetLock.blocked || !live || !this.onlineEnemyId || !this.isEnemyInRange(range)) {
      this.setActionText(this.targetLock.blocked || !live ? 'เป้าหมายหายแล้ว · แตะเลือกศัตรูใหม่ก่อนโจมตี' : 'ไกลเกินไป • เข้าใกล้' + this.activeEnemyDef.nameTh + 'ก่อน');
      return;
    }
    onlineSession.sendAttack(this.onlineEnemyId, action);
    this.setActionText(action === 'basic' ? 'ส่งการโจมตีไปยังเซิร์ฟเวอร์' : 'ส่งวิชาไปยังเซิร์ฟเวอร์');
  }
  private updateEnemyMovement(time: number, delta: number): void {
    if (!this.enemy || !this.player || this.combat.enemyHp <= 0) return;
    const dx = this.player.x - this.enemy.x;
    const distance = Math.abs(dx);
    const stopDistance = Math.max(54, this.activeEnemyDef.attackRange * 0.72);
    if (this.time.now < this.combat.enemyStunnedUntil) { this.enemyMotion.vx=0; return; }
    this.enemyMotion.x=this.enemy.x/this.worldScale;
    const dir=Math.sign(dx)||1;
    const depthDelta=this.motion.depth-this.enemyMotion.depth;
    this.enemyMotion.step(delta/1000,{axis:distance>stopDistance && !this.enemyTelegraphing?dir*this.activeEnemyDef.moveSpeed/260:0,depth:Math.abs(depthDelta)>5 && !this.enemyTelegraphing?Math.sign(depthDelta)*.35:0});
    this.enemy.x=this.enemyMotion.x*this.worldScale;
    this.enemy.scaleX=enemyFacingScale(this.enemyDisplayScale,dir);
    this.enemyBaseY=this.scale.height*this.groundRatio+(this.enemyMotion.depth-this.enemyMotion.height)*this.scale.height/720-(this.activeEnemyDef.id==='pop'?0:28);
    if (this.activeEnemyDef.movement === 'hover-chase') {
      this.enemy.y = this.enemyBaseY - 28 + Math.sin(time / 180) * 8;
    } else if (this.activeEnemyDef.movement === 'stalk') {
      this.enemy.y = this.enemyBaseY + Math.sin(time / 520) * 2;
    } else if (this.activeEnemyDef.movement === 'boss-stalk') {
      this.enemy.y = this.enemyBaseY - 14 + Math.sin(time / 700) * 4;
    } else {
      this.enemy.y = this.enemyBaseY;
    }
  }

  private syncEnemyHudPosition(): void {
    if (!this.enemy || !this.enemyLabel) return;
    this.enemyLabel.setPosition(this.enemy.x, this.enemy.y - (this.activeEnemyDef.boss ? 175 : this.activeEnemyDef.id==='pop'?175:155));
    if(this.combat.enemyHp>0)this.enemyVisual?.animate?.(this.game.loop.delta/1000,this.time.now,this.enemyMotion.vx,this.enemyTelegraphing);
  }

  private createHud(): void {
    const { width, height } = this.scale;
    this.playerHpBar = this.add.graphics().setDepth(25).setVisible(false);
    this.enemyHpBar = this.add.graphics().setDepth(25).setVisible(false);
    this.actionLabel = this.add.text(width / 2, height * 0.13, 'เดินเข้าใกล้แล้วโจมตี • J/Z หรือ ATK', { fontFamily: 'system-ui', fontSize: `${Phaser.Math.Clamp(width * 0.015, 12, 17)}px`, color: '#f2e2be', backgroundColor: 'rgba(9,16,28,.64)', padding: { x: 12, y: 8 } }).setOrigin(0.5).setDepth(30);
    this.comboLabel = this.add.text(width / 2, height * 0.22, 'COMBO 0', { fontFamily: 'system-ui', fontStyle: 'bold', fontSize: `${Phaser.Math.Clamp(width * 0.021, 16, 25)}px`, color: '#efc17a', stroke: '#111827', strokeThickness: 4 }).setOrigin(0.5).setDepth(30);
    this.cooldownLabel = this.add.text(width / 2, height - 26, '', { fontFamily: 'system-ui', fontSize: `${Phaser.Math.Clamp(width * 0.012, 10, 14)}px`, color: '#c9d7e9', backgroundColor: 'rgba(9,16,28,.55)', padding: { x: 10, y: 5 } }).setOrigin(0.5, 1).setDepth(30);
    this.actionLabel.setVisible(false);
    this.comboLabel.setVisible(false);
    this.cooldownLabel.setVisible(false);
    this.setDomText('hud-objective-enemy', isSanctuary(this.mapId)?'เขตปลอดภัย • ไม่มีผี':`เป้าหมาย: ${this.onlineEnemySnapshot?.displayName??this.activeEnemyDef.nameTh} • HP ${this.combat.enemyHp}/${this.combat.enemyMaxHp}`);
    const bossPanel = document.getElementById('hud-boss');
    if (bossPanel) bossPanel.hidden = !this.activeEnemyDef.boss;
  }

  private handleAction(action: GameAction): void {
    if (!this.player) return;
    const now = this.time.now;
    if(action.type==='sprint'){this.sprintHeld=action.active;return;}
    document.body.dataset.lastAction = action.type === 'skill' ? `skill-${action.slot + 1}` : action.type;
    const activeInput=action.type!=='move'||Math.abs(action.axis)>.05;if(activeInput){this.lastUserActivityAt=now;if(this.idleAutoRequested&&!document.hidden&&this.onlineEnabled){onlineSession.command('autoplay','off');this.idleAutoRequested=false;}}
    if (action.type === 'move') {
      this.moveAxis = action.axis;
      return;
    }
    if (action.type === 'move-depth') { this.moveDepth = action.axis; if(action.axis<-.1 && !this.characterUi?.open && GAME_MAPS[this.mapId].portals.some(p=>Math.abs(p.x-this.motion.x)<85))this.warp();return; }
    if (this.combat.playerHp <= 0 || this.characterUi?.open) return;
    if (action.type === 'attack') { this.doAttack(); return; }
    if (action.type === 'jump') { this.doJump(); return; }
    if (action.type === 'dodge') { this.doDodge(now); return; }
    if (action.type === 'pickup') { this.pickupNearestGroundDrop(); return; }
    this.doSkill(action.slot, now);
  }

  private doAttack(): void {
    if (isSanctuary(this.mapId) || this.hunterRig?.attacking) return;
    this.hunterRig?.attack(this.combat.combo % 3 === 2);
    const kind=GEAR.find(g=>g.id===this.character.equipment.weapon)?.kind;
    combatAudio.play(kind==='spear'||kind==='crossbow'?'stab':kind==='talisman'?'spell':'slash');
    if(this.onlineEnabled)onlineSession.command('pose','slash');
    this.time.delayedCall(85, () => this.resolveAttackContact());
  }

  private resolveAttackContact(): void {
    if (!this.player || this.combat.playerHp <= 0) return;
    const kind=GEAR.find(g=>g.id===this.character.equipment.weapon)?.kind??'knife';
    const range=basicWeaponRange(kind)*this.worldScale;
    if(kind==='bow'||kind==='crossbow'||kind==='spear'||kind==='talisman')classSkillFx(this,kind==='spear'?'spear-piercing-thrust':kind==='talisman'?'shaman-exorcism-talisman':'hunter-arrow',this.player.x,this.player.y,this.facing,this.enemy?.x);
    const slash=this.add.graphics().setDepth(13);
    slash.lineStyle(3,0xffd786,.9).beginPath();
    slash.arc(this.player.x+this.facing*22,this.player.y-47,29,this.facing>0?-1.3:1.8,this.facing>0?1.2:4.4);
    slash.strokePath();this.tweens.add({targets:slash,alpha:0,duration:160,onComplete:()=>slash.destroy()});
    if (!this.isEnemyInRange(range) || this.combat.enemyHp <= 0) { combatFeedback(this,this.player.x+this.facing*35,this.playerFeedbackY(),0,'miss');this.setActionText(`ไกลเกินไป • เข้าใกล้${this.activeEnemyDef.nameTh}ก่อน`); return; }
    if (this.onlineEnabled) { this.sendOnlineAttack('basic', range); return; }
    const contactHeight = 47 + this.motion.height - this.enemyMotion.height - (this.activeEnemyDef.movement === 'hover-chase' ? 28 : 0);
    const roll=Math.random(),result = this.combat.normalAttack(roll, contactHeight,roll);
    if(result.missed){combatFeedback(this,this.enemy!.x,this.enemyFeedbackY(),0,'miss');this.setActionText('MISS · ผีหลบพ้นคมอาวุธ');this.updateHud();return;}
    document.body.dataset.hitZone = contactHeight >= 95 ? 'head' : contactHeight < 35 ? 'legs' : 'body';
    this.hitFx(result.critical ? 0xffe088 : 0xf2b6a0);
    this.showDamage(result.damage, result.critical);
    this.setActionText(result.stage === 3 ? `FINISHER • ${result.damage}` : `HIT ${result.stage} • ${result.damage}`);
    this.afterEnemyDamage();
  }

  private doJump(): void {
    if (!this.player) return;
    this.motion.jump(this.moveDepth > .1);
    if(this.motion.grounded||this.motion.climbing)combatAudio.play('jump');
    this.setActionText(this.moveDepth > .1 ? 'ลงจากทางยกสูง' : 'กระโดด • W/S เปลี่ยนแนวทางเดิน');
  }

  private doDodge(now: number): void {
    if (!this.player) return;
    if(!this.combat.canDodge(now)||!requestDodge(this.endurance,now,this.combat.playerHp>0)){this.setActionText(this.endurance.stamina<18?'แรงไม่พอหลบ':'หลบกำลังพัก');return;}
    if (!this.combat.beginDodge(now)) { this.setActionText(`DODGE CD ${this.combat.remainingDodgeCooldown(now).toFixed(1)}s`); return; }
    this.setActionText('DODGE • EVADE');
    this.motion.dodge();
    combatAudio.play('dodge');
    if(this.onlineEnabled)onlineSession.command('dodge','');
  }

  private hotbarPresentation(slot:number,now:number){
    const binding=this.character.shortcuts[slot]??null,me=onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId),remote=this.onlineEnabled||this.waitingAuthority;
    const quantity=binding?.kind==='item'?(remote?(me?.inventory?.[binding.id]??0):binding.id==='potion'?this.character.potions:this.character.owned.includes(binding.id)?1:this.huntInventory.stacks[binding.id]??0):undefined;
    return hotbarState(binding,{profession:this.character.profession,level:this.combat.level,mana:this.combat.mana,readyAt:remote?(me?.cooldowns?.[binding?.id??'']??0):(this.combat.classReadyAt[binding?.id??'']??0),now:remote?Date.now():now,quantity,syncing:this.waitingAuthority});
  }

  private doSkill(slot: number, now: number): void {
    if(this.waitingAuthority){this.setActionText('รอข้อมูลยืนยันจากเซิร์ฟเวอร์');return;}
    const binding=this.character.shortcuts[slot];
    if(!binding){this.setActionText('ช่องว่าง · เปิดเมนู → จัดช่องลัด เพื่อใส่สกิลหรือไอเท็ม');return;}
    const availability=this.hotbarPresentation(slot,now);if(!availability.available){this.setActionText(availability.description);return;}
    if(binding.kind==='item'){
      if(binding.id==='capture-talisman'){requestGhostCapture(text=>this.setActionText(text));return;}
      combatAudio.play(binding.id==='potion'?'item':'equip');
      if(this.onlineEnabled){onlineSession.command(binding.id==='potion'?'potion':'equip',binding.id);return;}
      if(binding.id==='potion'){if(this.character.potions>0&&this.combat.playerHp<this.combat.playerMaxHp){if((this.combat.classReadyAt.potion??0)>now)return;this.character.potions--;this.combat.classReadyAt.potion=now+1500;this.combat.playerHp=Math.min(this.combat.playerMaxHp,this.combat.playerHp+40);this.playerFx(0x8ee0a5);}}
      else equip(this.character,binding.id);
      this.applyCharacter();this.updateHud();if(new URLSearchParams(location.search).get('qa')!=='game')try{localStorage.setItem('phi-character-v1',JSON.stringify(this.character));}catch{}return;
    }
    const skill=skillById(binding.id);if(!skill||skill.classId!==this.character.profession)return;
    if(skill.level>this.combat.level){this.setActionText(skill.name+' · ปลดที่ Lv.'+skill.level);return;}
    if(skill.effect!=='guard'&&skill.effect!=='summon'&&!this.isEnemyInRange(skill.range*this.worldScale)){this.setActionText(skill.name+' · ไกลเกินระยะ / ต่างชั้น');return;}
    if(this.onlineEnabled){if(this.targetLock.blocked&&skill.effect!=='guard'&&skill.effect!=='summon'){this.setActionText('เลือกเป้าหมายใหม่ก่อนใช้วิชา');return;}onlineSession.command('cast',JSON.stringify({id:skill.id,enemyId:this.onlineEnemyId??''}));return;}
    const before=this.combat.enemyHp;const hp=this.combat.playerHp;
    const result=this.combat.cast(skill,this.character.learned[skill.id]??0,now);
    if(!result.used){this.setActionText(skill.name+' · รอคูลดาวน์');return;}
    this.hunterRig?.attack();
    combatAudio.play(skill.effect==='heal'?'heal':skill.icon==='fire'?'fire':skill.icon==='guard'?'guard':skill.icon==='strike'?'stab':'spell');
    classSkillFx(this,skill.id,this.player!.x,this.player!.y,this.facing,this.enemy?.x);
    if(skill.effect==='guard'||skill.effect==='summon'){this.playerFx(0xb69aff);this.setActionText(skill.name);}
    if(this.onlineEnabled){this.combat.enemyHp=before;this.combat.playerHp=hp;onlineSession.command('cast',JSON.stringify({id:skill.id,enemyId:this.onlineEnemyId??''}));}
    if(result.healed){this.playerFx(0x8ee0a5);this.setActionText(skill.name+' · +'+result.healed+' HP');}
    else if(result.damage){this.hitFx(skill.icon==='fire'?0xff9a5c:0xb58cff);if(!this.onlineEnabled){this.showDamage(result.damage,false,true);this.afterEnemyDamage();}this.setActionText(skill.name);}
    this.updateHud();
  }

  private startTelegraph(): void {
    if (isSanctuary(this.mapId) || this.onlineEnabled || !this.enemy || !this.player || this.combat.enemyHp <= 0) return;
    if (this.time.now < this.combat.enemyStunnedUntil) { this.telegraphTimer=this.time.delayedCall(this.combat.enemyStunnedUntil-this.time.now,()=>this.startTelegraph()); return; }
    this.enemyTelegraphing = true;
    this.telegraph?.destroy();
    const radius = this.activeEnemyDef.boss ? 96 : Math.max(48, Math.min(76, this.activeEnemyDef.attackRange * 0.34));
    this.telegraph = this.add.graphics().setDepth(8);
    this.telegraph.lineStyle(this.activeEnemyDef.boss ? 7 : 5, this.activeEnemyDef.boss ? 0xffdb6b : 0xffc44d, 0.98)
      .strokeCircle(this.enemy.x, this.enemy.y - 10, radius);
    this.telegraph.fillStyle(this.activeEnemyDef.boss ? 0xa66cff : 0xff704d, this.activeEnemyDef.boss ? 0.16 : 0.12)
      .fillCircle(this.enemy.x, this.enemy.y - 10, radius + 2);
    this.setActionText(`⚠ ${this.activeEnemyDef.nameTh}กำลังโจมตี • หลบหรือออกจากระยะ!`);
    this.tweens.add({ targets: this.telegraph, alpha: 0.22, duration: this.activeEnemyDef.boss ? 220 : 160, yoyo: true, repeat: this.activeEnemyDef.boss ? 3 : 2 });
    this.telegraphTimer = this.time.delayedCall(this.activeEnemyDef.telegraphMs, () => {
      this.telegraph?.destroy(); this.telegraph = undefined;
      this.enemyTelegraphing = false;
      if (!this.enemy || !this.player || this.combat.enemyHp <= 0) return;
      const enemyFacing: -1 | 1 = this.player.x < this.enemy.x ? -1 : 1;
      if (Math.abs(this.motion.height-this.enemyMotion.height) > 55 || Math.abs(this.motion.depth-this.enemyMotion.depth) > 48) {
        this.setActionText('หลบพ้นแนวโจมตี');
        this.telegraphTimer = this.time.delayedCall(this.activeEnemyDef.attackCadenceMs, () => this.startTelegraph());
        return;
      }
      const result = this.combat.resolveEnemyStrike(this.time.now, this.player.x, this.enemy.x, enemyFacing, this.activeEnemyDef.attackRange);
      if (result.hit) {
        combatFeedback(this,this.player.x,this.playerFeedbackY(),result.damage,'received');
        this.setActionText(`โดน${this.activeEnemyDef.nameTh}โจมตี -${result.damage} HP`);
        this.playerFx(this.activeEnemyDef.boss ? 0xd989ff : 0xff7b72);
        this.hunterRig?.hit();
        if (this.combat.playerHp <= 0) { this.handlePlayerKo(); return; }
      } else {combatFeedback(this,this.player.x,this.playerFeedbackY(),0,'miss');this.setActionText('หลบสำเร็จ!');}
      this.updateHud();
      this.telegraphTimer = this.time.delayedCall(this.activeEnemyDef.attackCadenceMs, () => this.startTelegraph());
    });
  }

  private afterEnemyDamage(): void {
    this.updateHud();
    if (this.combat.enemyHp > 0 || !this.enemy) return;

    const defeated = this.activeEnemyDef;
    const levelBeforeReward=this.combat.level;
    const outcome = this.progressionLoop.defeatEnemy(defeated.id, this.time.now);
    if (!outcome.accepted) {
      this.setActionText(`${defeated.nameTh}ถูกกำจัด • ไม่มีรางวัลซ้ำ`);
      return;
    }

    const lootId = ghostContentId(defeated.id);
    const pityMisses = this.huntInventory.pityMisses[lootId] ?? 0;
    const loot = rollHuntReward(lootId, pityMisses, this.forceLoot ? () => 0 : Math.random);
    this.huntInventory = applyHuntItems(this.huntInventory, loot);
    if(loot.items.length)combatAudio.play('loot');
    if(this.progressionLoop.progression.level>levelBeforeReward)combatAudio.play('level');
    this.lastLootText = loot.items.map((item) => `${item.nameTh} ×${item.quantity}`).join(' • ');
    document.body.dataset.lastLoot = this.lastLootText;
    document.body.dataset.inventoryKrasueAppendix = String(this.huntInventory.stacks['krasue-appendix'] ?? 0);
    document.body.dataset.inventoryKrahangFragment = String(this.huntInventory.stacks['krahang-winnowing-fragment'] ?? 0);

    this.combat.setLevel(outcome.progression.level);
    ghostDeath(this,this.enemy,this.activeEnemyDef.id,false,this.scale.height*this.groundRatio+this.enemyMotion.depth*this.scale.height/720);
    this.enemyTelegraphing = false;
    this.telegraph?.destroy();
    this.telegraph = undefined;
    this.telegraphTimer?.remove(false);
    this.updateHud();

    if (outcome.bossSpawned) {
      this.setActionText(`อาถรรพ์ 100% • แม่ตานีพันปีปรากฏตัว!`);
      this.resetTimer = this.time.delayedCall(900, () => this.activateEnemy('mae-tanee'));
      return;
    }

    if (outcome.bossDefeated) {
      this.setActionText(`ปราบแม่ตานีพันปี • +${outcome.reward.exp} EXP • +${outcome.reward.bie} เบี้ย • +${outcome.reward.spirit} เศษวิญญาณ${this.lastLootText ? ` • ${this.lastLootText}` : ''}`);
      this.resetTimer = this.time.delayedCall(CLEANSING_DELAY_MS, () => {
        const reset = this.progressionLoop.tick(this.time.now);
        if (!reset.cleansingCompleted) return;
        this.activateEnemy(this.nextNormalEnemy());
        this.setActionText(`ชำระอาถรรพ์สำเร็จ • วงรอบ ${reset.world.cycle + 1} เริ่มแล้ว`);
      });
      return;
    }

    this.setActionText(`${defeated.nameTh}ถูกกำจัด • +${outcome.reward.exp} EXP • +${outcome.reward.bie} เบี้ย${this.lastLootText ? ` • ${this.lastLootText}` : ''}`);
    if (this.holdLootQa) return;
    this.resetTimer = this.time.delayedCall(1650, () => {
      this.activateEnemy(this.nextNormalEnemy());
      this.setActionText('ผีชุดใหม่เข้ามาในพื้นที่');
    });
  }

  private activateEnemy(id: EnemyId): void {
    this.telegraphTimer?.remove(false);
    this.telegraph?.destroy();
    this.telegraph = undefined;
    this.enemyTelegraphing = false;
    this.enemy?.destroy(true);
    this.enemy = undefined;
    this.enemyLabel?.destroy();
    this.enemyLabel = undefined;

    this.activeEnemyDef = ENEMIES[id];
    document.body.dataset.enemyId = id;
    document.body.classList.toggle('boss-active', this.activeEnemyDef.boss);
    this.combat.configureEnemy({ enemyMaxHp: this.activeEnemyDef.maxHp, enemyDamage: this.activeEnemyDef.damage });
    this.createEnemy();
    this.enemyBirthEffect();
    const bossPanel = document.getElementById('hud-boss');
    if (bossPanel) bossPanel.hidden = !this.activeEnemyDef.boss;
    this.updateHud();
    this.telegraphTimer = this.time.delayedCall(900, () => this.startTelegraph());
  }

  private enemyBirthEffect(x?:number):void {if(this.enemy)spiritEffect(this,x??this.enemy.x,this.enemy.y,true,Math.abs(this.enemy.scaleY));}

  private nextNormalEnemy(): EnemyId {
    const ids: EnemyId[] = ['pop', 'krasue', 'tanee'];
    const id = ids[this.normalEnemyIndex % ids.length] ?? 'pop';
    this.normalEnemyIndex += 1;
    return id;
  }

  private handlePlayerKo(): void {
    if(!this.deathDeadline){combatAudio.play('ko');this.deathDeadline=this.time.now+30000;}
    this.moveAxis=0;this.moveDepth=0;this.motion.vx=0;this.motion.vz=0;
    this.telegraphTimer?.remove(false);this.hunterRig?.setDead(true);
    this.updateDeathState();
  }

  private reviveWithItem():void {
    if(this.combat.playerHp>0)return;
    if(this.onlineEnabled){onlineSession.command('revive','nine-lives');return;}
    if((this.huntInventory.stacks['nine-lives']??0)<1)return;
    this.huntInventory.stacks['nine-lives']!--;
    this.combat.resetPlayer();this.combat.evadingUntil=this.time.now+3000;
    this.deathDeadline=0;this.hunterRig?.setDead(false);
    if(this.player)spiritEffect(this,this.player.x,this.player.y,true);
    combatAudio.play('respawn');this.updateDeathState();this.updateHud();this.updateProgressionHud();
    if(this.combat.enemyHp>0)this.telegraphTimer=this.time.delayedCall(1400,()=>this.startTelegraph());
  }

  private updateDeathState():void {
    if(this.combat.playerHp>0){this.deathDeadline=0;this.deathPanel?.remove();this.deathPanel=undefined;document.body.dataset.deathState='alive';return;}
    if(!this.deathDeadline)this.deathDeadline=this.time.now+30000;
    const me=onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId);
    const remaining=this.onlineEnabled&&me?.respawnAt!==undefined?me.respawnAt-Date.now():this.deathDeadline-this.time.now;
    const count=this.onlineEnabled?(me?.inventory?.['nine-lives']??0):(this.huntInventory.stacks['nine-lives']??0);
    if(!this.deathPanel){
      const panel=document.createElement('div');panel.id='death-panel';panel.setAttribute('role','status');
      panel.style.cssText='position:fixed;z-index:80;left:50%;top:14%;transform:translateX(-50%);width:min(340px,90vw);box-sizing:border-box;background:rgba(17,14,27,.94);border:1px solid #b9a2d5;border-radius:12px;padding:12px;text-align:center;color:#f1e7ff;font-family:Noto Sans Thai,system-ui;pointer-events:auto';
      panel.innerHTML='<strong>สิ้นพลัง • ร่างยังอยู่ที่เดิม</strong><p id="death-countdown" style="margin:6px 0"></p><button id="death-revive" style="font:inherit;min-height:40px;padding:6px 12px;border-radius:8px">ใช้เก้าชีวิต</button><small style="display:block;margin-top:6px">คืนชีพที่เดิมเต็ม HP • ใช้ 1 ชิ้น</small>';
      panel.querySelector('button')!.addEventListener('click',()=>this.reviveWithItem());document.body.append(panel);this.deathPanel=panel;
    }
    this.deathPanel.querySelector('p')!.textContent='กลับเมืองใน '+Math.max(0,Math.ceil(remaining/1000))+' วินาที';
    const button=this.deathPanel.querySelector('button')!;button.disabled=count<1;button.textContent='ใช้เก้าชีวิต • มี '+count+' ชิ้น';
    document.body.dataset.deathState='dead';document.body.dataset.deathSeconds=String(Math.max(0,Math.ceil(remaining/1000)));
    if(!this.onlineEnabled&&remaining<=0){this.combat.resetPlayer();this.deathDeadline=0;document.body.dataset.deathState='alive';this.mapId='town';this.warpSpawn=260;this.scene.restart({resizing:true,warp:true});}
  }

  private isEnemyInRange(range: number): boolean {
    return !!this.player && !!this.enemy && Math.abs(this.motion.height-this.enemyMotion.height) < 120 && Math.abs(this.motion.depth-this.enemyMotion.depth) < 48 && inHorizontalRange(this.player.x, this.enemy.x, this.facing, range);
  }

  private updateHud(): void {
    if (!this.playerHpBar || !this.enemyHpBar) return;
    const { width } = this.scale;
    const pw = Math.min(260, width * 0.28);
    this.playerHpBar.clear().fillStyle(0x101722, .9).fillRect(24, 56, pw, 17).fillStyle(0x5fbe7b).fillRect(26, 58, (pw - 4) * (this.combat.playerHp / this.combat.playerMaxHp), 13).lineStyle(2, 0xe7d7b6, .65).strokeRect(24, 56, pw, 17);
    if(this.enemy && this.enemyLabel){
    const ew = Math.min(220, width * 0.25);
    this.enemyHpBar.clear().fillStyle(0x101722, .9).fillRect(this.enemy.x - ew / 2, this.enemy.y - 60, ew, 12).fillStyle(0xc96a5d).fillRect(this.enemy.x - ew / 2 + 2, this.enemy.y - 58, (ew - 4) * (this.combat.enemyHp / this.combat.enemyMaxHp), 8);
    const compact = this.scale.height < 560;
    const enemyIdentity = `${this.activeEnemyDef.boss ? 'BOSS · ' : 'ศัตรู · '}${this.onlineEnemySnapshot?.displayName??this.activeEnemyDef.nameTh}`;
    this.enemyLabel.setText(compact ? enemyIdentity : `${enemyIdentity} • HP ${this.combat.enemyHp}/${this.combat.enemyMaxHp}`).setFontSize(compact ? 12 : 16);
    this.enemyLabel.setVisible(this.combat.enemyHp > 0 && !this.activeEnemyDef.boss && !this.onlineEnemySnapshot?.archetypeId);
    this.enemyHpBar.setVisible(false);
    }
    this.comboLabel?.setText(`COMBO ${this.combat.combo}`);
    renderResourceBar('hud-player-hp','HP',this.combat.playerHp,this.combat.playerMaxHp,{lowAt:.25});
    this.setDomText('hud-objective-enemy', isSanctuary(this.mapId)?'เขตปลอดภัย • ไม่มีผี':`เป้าหมาย: ${this.onlineEnemySnapshot?.displayName??this.activeEnemyDef.nameTh} • HP ${this.combat.enemyHp}/${this.combat.enemyMaxHp}`);
    const combo = document.getElementById('hud-combo');
    if (combo) combo.textContent = this.combat.combo > 1 ? `COMBO ×${this.combat.combo}` : '';
    if (this.activeEnemyDef.boss) {
      this.setDomText('hud-boss-name', this.onlineEnemySnapshot?.displayName??this.activeEnemyDef.nameTh);
      this.setDomText('hud-boss-hp-text', `${this.combat.enemyHp} / ${this.combat.enemyMaxHp}`);
      this.setDomWidth('hud-boss-hp-fill', this.combat.enemyHp / this.combat.enemyMaxHp);
    }
    this.updateProgressionHud();
    this.refreshHuntPrepUi();
    this.syncQaState();
  }

  private installHuntPrepUi(openOnStart = false): void {
    const panel = document.getElementById('hunt-prep-panel') as HTMLElement | null;
    const toggle = document.getElementById('hunt-prep-toggle') as HTMLButtonElement | null;
    const close = document.getElementById('hunt-prep-close') as HTMLButtonElement | null;
    const craft = document.getElementById('hunt-craft-arrow') as HTMLButtonElement | null;
    const upgrade = document.getElementById('hunt-upgrade-bow') as HTMLButtonElement | null;
    if (!panel || !toggle) return;
    const setOpen = (open: boolean) => {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.dataset.huntPrepOpen = open ? '1' : '0';
      if (open) this.refreshHuntPrepUi();
    };
    toggle.onclick = () => setOpen(panel.hidden);
    if (close) close.onclick = () => setOpen(false);
    if (craft) craft.onclick = () => this.craftKrasueSpiritArrows();
    if (upgrade) upgrade.onclick = () => this.upgradeWeatheredHunterBow();
    setOpen(openOnStart);
  }

  private craftKrasueSpiritArrows(): void {
    if (this.onlineEnabled) {
      this.setActionText('ออนไลน์: ใช้ของที่เซิร์ฟเวอร์ถือครองจากหน้าตัวละคร');
      return;
    }
    const progression = this.progressionLoop.progression;
    const result = craftRecipe(
      this.huntInventory,
      createWallet({ bie: progression.bie, spiritEssence: progression.spiritEssence }),
      'krasue-spirit-arrow',
    );
    if (!result.ok) {
      const reason = result.reason === 'insufficient-bie'
        ? 'เบี้ยไม่พอ'
        : result.reason === 'missing-material'
          ? 'วัตถุดิบไม่พอ'
          : result.reason === 'insufficient-spirit'
            ? 'เศษวิญญาณไม่พอ'
            : 'คราฟไม่ได้';
      this.setActionText('คราฟไม่สำเร็จ • ' + reason);
      this.refreshHuntPrepUi();
      return;
    }
    this.huntInventory = result.inventory;
    this.progressionLoop.replaceCurrencies(result.wallet.bie, result.wallet.spiritEssence);
    this.setActionText('คราฟสำเร็จ • ' + (result.output?.nameTh ?? 'ลูกธนูเอ็นกระสือ') + ' ×' + (result.output?.quantity ?? 30));
    this.updateHud();
  }

  private upgradeWeatheredHunterBow(): void {
    if (this.onlineEnabled) {
      this.setActionText('ออนไลน์: การอัปเกรดต้องยืนยันโดยเซิร์ฟเวอร์');
      return;
    }
    const progression = this.progressionLoop.progression;
    const result = upgradeEquipment(
      this.huntInventory,
      createWallet({ bie: progression.bie, spiritEssence: progression.spiritEssence }),
      'hunter-bow-weathered',
    );
    if (!result.ok) {
      const reason = result.reason === 'item-not-owned'
        ? 'ยังไม่มีธนูพรานผุกร่อน'
        : result.reason === 'insufficient-bie'
          ? 'เบี้ยไม่พอ'
          : result.reason === 'missing-material'
            ? 'วัตถุดิบอัปเกรดไม่พอ'
            : result.reason === 'max-level'
              ? 'อัปเกรดเต็มแล้ว'
              : 'อัปเกรดไม่ได้';
      this.setActionText('อัปเกรดไม่ได้ • ' + reason);
      this.refreshHuntPrepUi();
      return;
    }
    this.huntInventory = result.inventory;
    this.progressionLoop.replaceCurrencies(result.wallet.bie, result.wallet.spiritEssence);
    this.setActionText(result.success
      ? 'อัปเกรดสำเร็จ • ธนูพราน +' + result.level
      : 'อัปเกรดพลาด • ของไม่แตก • Blessing ' + result.blessing + '%');
    this.updateHud();
  }

  private refreshHuntPrepUi(): void {
    const progression = this.progressionLoop.progression;
    const stack = (itemId: string) => Math.max(0, Math.trunc(this.huntInventory.stacks[itemId] ?? 0));
    this.setDomText('hunt-prep-bie', progression.bie.toLocaleString('th-TH'));
    this.setDomText('hunt-prep-spirit', progression.spiritEssence.toLocaleString('th-TH'));
    this.setDomText('hunt-item-appendix', stack('krasue-appendix').toString());
    this.setDomText('hunt-item-thread', stack('krasue-spirit-thread').toString());
    this.setDomText('hunt-item-arrows', stack('krasue-spirit-arrow').toString());
    const signatureCount = stack('krasue-appendix') + stack('krahang-winnowing-fragment');
    this.setDomText('hunt-prep-badge', signatureCount.toString());

    const bowOwned = stack('hunter-bow-weathered') > 0;
    const bowState = this.huntInventory.equipment['hunter-bow-weathered'];
    const level = bowState?.level ?? 0;
    const blessing = bowState?.blessing ?? 0;
    this.setDomText('hunt-bow-level', bowOwned ? '+' + level + ' • Blessing ' + blessing + '%' : 'ยังไม่มี');

    const targetLevel = Math.min(10, level + 1);
    const cost = (THAI_HUNT_ECONOMY.upgrade.bieByTargetLevel as Record<string, number>)[String(targetLevel)] ?? 0;
    this.setDomText('hunt-upgrade-cost', bowOwned && level < 10 ? 'ขั้นถัดไป +' + targetLevel + ' • ' + cost + ' เบี้ย' : bowOwned ? 'อัปเกรดเต็มแล้ว' : 'หาได้จากกระสือ');

    const craftButton = document.getElementById('hunt-craft-arrow') as HTMLButtonElement | null;
    if (craftButton) {
      craftButton.disabled = this.onlineEnabled
        || progression.bie < 250
        || stack('krasue-appendix') < 1
        || stack('krasue-spirit-thread') < 2;
    }
    const upgradeButton = document.getElementById('hunt-upgrade-bow') as HTMLButtonElement | null;
    if (upgradeButton) upgradeButton.disabled = this.onlineEnabled || !bowOwned || level >= 10;

    const readiness = huntReadiness('krahang', 'bow', this.huntInventory);
    const ready = readiness?.ready ?? false;
    const readyNode = document.getElementById('hunt-krahang-readiness');
    if (readyNode) {
      readyNode.textContent = ready ? 'พร้อมล่ากระหัง ✓ • แผนธนู' : 'ยังไม่พร้อมล่ากระหัง • ต้องมีลูกธนูเอ็นกระสือ';
      readyNode.classList.toggle('ready', ready);
    }
    const hint = document.getElementById('hunt-source-hint');
    if (hint) hint.textContent = ready
      ? 'ของแก้ทางพร้อมแล้ว • กระหังแพ้ธนู/ทวน'
      : 'ล่ากระสือ → ไส้ติ่ง + เอ็นวิญญาณ → คราฟลูกธนู';

    document.body.dataset.huntArrows = stack('krasue-spirit-arrow').toString();
    document.body.dataset.huntKrahangReady = ready ? '1' : '0';
    document.body.dataset.huntBowLevel = level.toString();
    document.body.dataset.huntBowBlessing = blessing.toString();
  }

  private updateProgressionHud(): void {
    const progression = this.progressionLoop.progression;
    const world = this.progressionLoop.world;
    const required = expRequiredForLevel(progression.level);
    const levelNode = document.getElementById('hud-level');
    if (levelNode) levelNode.textContent = `Lv.${progression.level}`;
    renderResourceBar('hud-exp','EXP',progression.exp,required,{atCap:progression.level>=MAX_LEVEL});
    this.setDomText('hud-bie', progression.bie.toLocaleString('th-TH'));
    this.setDomText('hud-spirit-essence', progression.spiritEssence.toLocaleString('th-TH'));
    this.setDomText('hud-haunting-text', world.haunting.toString());
    this.setDomWidth('hud-haunting-fill', world.haunting / 100);
    if(!this.onlineEnabled && new URLSearchParams(location.search).get('qa')!=='game')try{localStorage.setItem('phi-offline-progress-v1',JSON.stringify({progression,inventory:this.huntInventory}));}catch{}
  }

  private updateCooldowns(now: number): void {
    renderResourceBar('hud-mana','SP',this.combat.mana,100,{lowAt:.2});
    renderResourceBar('hud-stamina','แรง',this.endurance.stamina,100,{lowAt:.18});
    const sprintCd=Math.max(0,(this.endurance.sprintReadyAt-this.time.now)/1000);
    const staminaBar=document.getElementById('hud-stamina-fill')?.parentElement;if(staminaBar)staminaBar.title=this.endurance.sprinting?'กำลังวิ่ง':sprintCd>0?'พักวิ่ง '+sprintCd.toFixed(1)+'s':'แรงสำหรับวิ่งและหลบ';
    this.setDomText('touch-sprint-state',sprintCd>0?sprintCd.toFixed(1)+'s':this.endurance.sprinting?'วิ่งไว':'วิ่ง');
    const skills = [0, 1, 2, 3].map((slot) => { const binding=this.character.shortcuts[slot];const skill=binding?.kind==='skill'?skillById(binding.id):undefined;const cd=Math.max(0,((this.combat.classReadyAt[binding?.id??'']??0)-now)/1000); return `S${slot + 1}:${cd > 0 ? cd.toFixed(1) : 'READY'}`; });
    const dodge = this.combat.remainingDodgeCooldown(now);
    this.cooldownLabel?.setText(`${skills.join('  ')}  •  DODGE:${dodge > 0 ? dodge.toFixed(1) : 'READY'}`);
    const statusPlayer=this.onlineEnabled?onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId):undefined;
    const statusNow=this.onlineEnabled?Date.now():now;
    updateWardUi(this.onlineEnabled?(statusPlayer?.wardUntil??0):this.combat.wardUntil,statusNow);
    for (let slot = 0; slot < 4; slot += 1) {
      const binding=this.character.shortcuts[slot];const skill=binding?.kind==='skill'?skillById(binding.id):undefined;const cd=Math.max(0,((this.combat.classReadyAt[binding?.id??'']??0)-now)/1000);
      const node = document.querySelector<HTMLButtonElement>(`[data-hud-skill="${slot}"]`);
      const state=this.hotbarPresentation(slot,now);
      const locked=state.locked;
      const editing=document.body.classList.contains('hotbar-editing');
      const title=binding?.kind==='item'?(binding.id==='capture-talisman'?'จับผี':binding.id==='potion'?'น้ำยา':GEAR.find(g=>g.id===binding.id)?.name??binding.id):skill?.name??'+';
      const iconPath=shortcutIcon(binding??null);
      if(node){node.querySelector('.skill-rune')!.textContent=title;node.querySelector('small')!.textContent=this.character.shortcutKeys[slot]!.toUpperCase();node.setAttribute('aria-label',title+(locked?' ปลดที่ Lv.'+skill!.level:''));node.style.backgroundImage=iconPath?`url('${iconPath}')`:'none';}
      if(node){node.dataset.itemQuantity=state.quantity?.toString()??'';node.disabled=!editing&&!state.available;node.dataset.skillCost=state.cost?.toString()??'';node.dataset.skillState=state.state;node.title=state.description;node.setAttribute('aria-label',skill?state.description:title+' · '+state.description);}
      node?.classList.toggle('empty-slot',!binding);
      if(node)node.draggable=!!binding&&document.body.classList.contains('hotbar-editing');
      node?.classList.toggle('locked',locked);
      node?.classList.toggle('cooling', state.seconds > 0||state.state==='unaffordable');
      this.setDomText(`hud-skill-cd-${slot}`,state.overlay);
      const touch = document.querySelector<HTMLElement>(`.action-cluster [data-game-action="skill-${slot + 1}"]`);
      touch?.classList.toggle('cooling', state.seconds > 0||state.state==='unaffordable');
      touch?.classList.toggle('empty-slot',!binding);
      if(touch)touch.draggable=!!binding&&document.body.classList.contains('hotbar-editing');
      if(touch){touch.querySelector('span')!.textContent=title;touch.setAttribute('aria-label',title+(locked?' ปลดที่ Lv.'+skill!.level:''));touch.style.backgroundImage=iconPath?`url('${iconPath}')`:'none';touch.dataset.cooldown=locked?'Lv.'+skill!.level:cd>0?cd.toFixed(1):'';touch.classList.toggle('locked',locked);}
      if(touch){(touch as HTMLButtonElement).disabled=!editing&&!state.available;touch.dataset.itemQuantity=state.quantity?.toString()??'';touch.dataset.skillCost=state.cost?.toString()??'';touch.dataset.skillState=state.state;touch.dataset.cooldown=state.overlay;touch.title=state.description;touch.setAttribute('aria-label',skill?state.description:title+' · '+state.description);}
    }
    const dodgeNode = document.querySelector<HTMLElement>('.hud-dodge');
    dodgeNode?.classList.toggle('cooling', dodge > 0);
    this.setDomText('hud-dodge-cd', dodge > 0 ? dodge.toFixed(1) : '');
    const touchDodge = document.querySelector<HTMLElement>('.action-cluster [data-game-action="dodge"]');
    touchDodge?.classList.toggle('cooling', dodge > 0);
    if (touchDodge) touchDodge.dataset.cooldown = dodge > 0 ? dodge.toFixed(1) : '';
  }

  private showDamage(amount: number, critical: boolean, skill = false): void {
    if (!this.enemy) return;
    combatFeedback(this,this.enemy.x,this.enemyFeedbackY(),amount,critical?'critical':'damage');void skill;
  }

  private enemyFeedbackY():number {return (this.enemy?.y??0)-(this.activeEnemyDef.boss?225:170)*Math.abs(this.enemy?.scaleY??1)-34;}
  private playerFeedbackY():number {return (this.player?.y??0)-135*Math.abs(this.hunterRig?.container.scaleY??1)-34;}

  private hitFx(color: number): void {
    if (!this.enemy) return;
    this.combat.enemyStunnedUntil = this.time.now + 100;
    if (!this.onlineEnabled) {
      this.telegraphTimer?.remove(false); this.telegraph?.destroy(); this.telegraph=undefined; this.enemyTelegraphing=false;
      if(this.combat.enemyHp>0)this.telegraphTimer=this.time.delayedCall(100,()=>this.startTelegraph());
    }
    const burst = this.add.circle(this.enemy.x, this.enemy.y - 8, 45, color, .3).setDepth(7);
    this.tweens.add({ targets: burst, scale: 1.6, alpha: 0, duration: 260, onComplete: () => burst.destroy() });
    this.tweens.add({ targets: this.enemy, x: this.enemy.x + 8 * this.facing, duration: 55, yoyo: true, repeat: 1 });
  }

  private playerFx(color: number): void {
    if (!this.player) return;
    const burst = this.add.circle(this.player.x, this.player.y - 10, 42, color, .28).setDepth(7);
    this.tweens.add({ targets: burst, scale: 1.45, alpha: 0, duration: 280, onComplete: () => burst.destroy() });
  }

  private syncEnemyTargets(enemies:OnlineEnemy[]):void {
    const live=enemies.filter(e=>e.mapId===this.mapId&&e.hp>0),ids=new Set(live.map(e=>e.id));
    for(const [id,zone]of this.enemyTargetZones)if(!ids.has(id)){zone.destroy();this.enemyTargetZones.delete(id);}
    for(const e of live){let zone=this.enemyTargetZones.get(e.id);if(!zone){zone=this.add.zone(0,0,80,170).setOrigin(.5,1).setInteractive();this.enemyTargetZones.set(e.id,zone);registerActorTarget(this,zone,'enemy',e.id,()=>{const target=onlineSession.current.snapshot?.enemies.find(t=>t.id===e.id&&t.mapId===this.mapId&&t.hp>0);if(!target)return;this.targetLock.select(target.id);try{sessionStorage.setItem('phi-target-reselect:'+onlineSession.current.roomId,'1');}catch{}this.applyOnlineEnemy(target);document.body.dataset.enemyTargetId=target.id;this.setActionText('เลือก · ศัตรู · '+(target.displayName??ENEMIES[target.kind].nameTh));},()=>!!onlineSession.current.snapshot?.enemies.some(t=>t.id===e.id&&t.mapId===this.mapId&&t.hp>0));}zone.setPosition(e.x*this.worldScale,this.scale.height*this.groundRatio+(e.activity?.depth??0)*this.scale.height/720).setDepth(9.1+(e.activity?.depth??0)/100);}
    const top=this.cameras.main.getWorldPoint(0,0);document.body.dataset.enemyTargetHitPoints=JSON.stringify([...this.enemyTargetZones].map(([id,z])=>({id,x:z.x-top.x,y:z.y-top.y-50})));
  }

  private selectWorldIdentity(clicked:OnlinePlayer):void {
    const selected=(onlineSession.current.snapshot?.players??[]).find(p=>p.id===clicked.id&&p.mapId===this.mapId&&(p.hp??1)>0);if(!selected)return;
    this.selectedWorldActor=selected.id;
    document.body.dataset.selectedActorName=selected.name;document.body.dataset.selectedActorId=selected.id;
    this.setActionText(`เลือก · ${selected.name} · Lv.${selected.progression.level}`);
  }

  private arrangeWorldLabels():void {
    if(!this.player)return;
    const camera=this.cameras.main,local=onlineSession.current.playerId,prefs=getPreferences();
    const top=camera.getWorldPoint(6,6),bottom=camera.getWorldPoint(this.scale.width-6,this.scale.height-6);
    const viewport:LabelRect={x:top.x,y:top.y,width:bottom.x-top.x,height:bottom.y-top.y};
    const labels:ActorLabel[]=[],objects=new Map<string,Phaser.GameObjects.Text>(),reserved:LabelRect[]=[];
    const snapshot=onlineSession.current.snapshot,party=snapshot?.parties?.find(p=>p.members.includes(local??''));
    for(const player of snapshot?.players??[]){
      const own=player.id===local,label=own?this.playerNameplate:this.remotePlayers.get(player.id)?.nameplate;
      if(!label||player.mapId!==this.mapId)continue;
      const selected=player.id===this.selectedWorldActor,member=party?.members.includes(player.id)??false;
      const cue=actorRoleCue(playerActorRelation(snapshot??undefined,local??'',player.id,this.selectedWorldActor));
      if(own&&this.playerRoleMark&&this.groundShadow){drawActorRoleCue(this.playerRoleMark,cue,(this.scale.height<560?Math.max(.65,this.scale.height/500):1.35)*(1+this.motion.depth/600));this.playerRoleMark.setPosition(this.groundShadow.x,this.groundShadow.y).setDepth(this.groundShadow.depth+.01).setVisible((player.hp??1)>0);}
      else this.remotePlayers.get(player.id)?.showRole((player.hp??1)>0?cue:{...cue,shape:'none'});
      if((player.hp??1)<=0||(!prefs.showNames&&!selected)){label.setVisible(false);continue;}
      const full=cue.prefix+player.name+' · Lv.'+player.progression.level;
      label.setText(full).setColor(cue.textColor);
      label.setText(fitActorLabelText(full,Math.min(360,viewport.width-12),text=>label.context.measureText(text).width+12));
      const bounds=label.getBounds();
      labels.push({id:player.id,x:bounds.x,y:bounds.y,width:bounds.width,height:bounds.height,priority:own?100:selected?95:member?80:20,required:own||selected,...(this.worldLabelOffsets.has(player.id)?{previous:this.worldLabelOffsets.get(player.id)!}:{})});
      objects.set(player.id,label);
    }
    const enemies=(snapshot?.enemies??[]).filter(e=>e.mapId===this.mapId&&e.hp>0),enemyIds=new Set(enemies.map(e=>e.id));
    for(const [id,mark]of this.enemyRoleMarks)if(!enemyIds.has(id)){mark.destroy();this.enemyRoleMarks.delete(id);}
    for(const enemy of enemies){let mark=this.enemyRoleMarks.get(enemy.id);if(!mark){mark=this.add.graphics();this.enemyRoleMarks.set(enemy.id,mark);}drawActorRoleCue(mark,actorRoleCue({hostile:true,boss:enemy.boss,selected:enemy.id===this.onlineEnemyId}),this.scale.height<560?Math.max(.65,this.scale.height/500):1.35);mark.setPosition(enemy.x*this.worldScale,this.scale.height*this.groundRatio+(enemy.activity?.depth??0)*this.scale.height/720).setDepth(7.02+(enemy.activity?.depth??0)/100);}
    document.body.dataset.actorRoles=JSON.stringify([...(snapshot?.players??[]).filter(p=>p.mapId===this.mapId).map(p=>({id:p.id,...actorRoleCue(playerActorRelation(snapshot??undefined,local??'',p.id,this.selectedWorldActor))})),...enemies.map(e=>({id:e.id,...actorRoleCue({hostile:true,boss:e.boss,selected:e.id===this.onlineEnemyId})}))]);
    const reserve=(object:Phaser.GameObjects.GameObject):void=>{
      if(object instanceof Phaser.GameObjects.Text&&object.visible)reserved.push(object.getBounds());
      else if(object instanceof Phaser.GameObjects.Container&&object.visible)for(const child of object.list)reserve(child);
    };
    if(this.enemyLabel?.visible)reserved.push(this.enemyLabel.getBounds());
    if(this.enemyVisual)reserve(this.enemyVisual.container);
    for(const visual of this.extraEnemies.values())reserve(visual.container);
    for(const object of this.children.list)if(object.getData('label-reserved'))reserve(object);
    for(const label of [this.actionLabel,this.comboLabel,this.cooldownLabel,this.portalHint])if(label?.visible){const rect=label.getBounds(),a=camera.getWorldPoint(rect.left,rect.top),b=camera.getWorldPoint(rect.right,rect.bottom);reserved.push({x:a.x,y:a.y,width:b.x-a.x,height:b.y-a.y});}
    // HUD elements use CSS pixels; map them through the actual canvas scale and camera.
    const canvas=this.game.canvas.getBoundingClientRect(),sx=this.scale.width/canvas.width,sy=this.scale.height/canvas.height;
    if(performance.now()-this.worldHudCheck>120){
      this.worldHudCheck=performance.now();
      this.worldHudRects=Array.from(document.querySelectorAll<HTMLElement>('.hud-frame,.network-status,.rpg-menu-toggle,.npc-talk,.town-context')).filter(el=>getComputedStyle(el).visibility!=='hidden').map(el=>el.getBoundingClientRect()).filter(rect=>rect.width>0&&rect.height>0);
    }
    for(const rect of this.worldHudRects){
      const a=camera.getWorldPoint((rect.left-canvas.left)*sx,(rect.top-canvas.top)*sy),b=camera.getWorldPoint((rect.right-canvas.left)*sx,(rect.bottom-canvas.top)*sy);
      reserved.push({x:a.x,y:a.y,width:b.x-a.x,height:b.y-a.y});
    }
    const placements=layoutActorLabels(labels,reserved,viewport);
    for(const placement of placements){
      const label=objects.get(placement.id)!;
      label.setVisible(placement.visible).setPosition(placement.x+label.width/2,placement.y+label.height/2);
      if(placement.visible)this.worldLabelOffsets.set(placement.id,{dx:placement.dx,dy:placement.dy});
    }
    const screenOrigin=camera.getWorldPoint(0,0);
    document.body.dataset.actorHitPoints=JSON.stringify([{id:local,x:this.player.x,y:this.player.y-50},...Array.from(this.remotePlayers,([id,a])=>({id,x:a.container.x,y:a.container.y-50}))].map(p=>({...p,x:(p.x-screenOrigin.x)*camera.zoom,y:(p.y-screenOrigin.y)*camera.zoom})));
    document.body.dataset.visibleActorLabels=String(placements.filter(p=>p.visible).length);
    document.body.dataset.actorLabelRects=JSON.stringify(placements.map(p=>({id:p.id,visible:p.visible,x:(p.x-screenOrigin.x)*camera.zoom,y:(p.y-screenOrigin.y)*camera.zoom,width:(objects.get(p.id)?.width??0)*camera.zoom,height:(objects.get(p.id)?.height??0)*camera.zoom,dx:p.dx,dy:p.dy})));
  }

  private setActionText(text: string): void {
    if(/ไกลเกิน|คูลดาวน์|ไม่ได้|ก่อนวาร์ป|ช่องว่าง|ปลดที่|DODGE CD/.test(text))combatAudio.play('deny');
    this.actionLabel?.setText(text);
    this.setDomText('hud-combat-toast', text);
    this.characterUi?.log(text);
  }

  private setDomText(id: string, text: string): void {
    const node = document.getElementById(id);
    if (node) node.textContent = text;
  }

  private setDomWidth(id: string, ratio: number): void {
    const node = document.getElementById(id) as HTMLElement | null;
    if (node) node.style.width = `${Phaser.Math.Clamp(ratio, 0, 1) * 100}%`;
  }

  private syncQaState(): void {
    if (!this.player) return;
    const playerDot = document.querySelector<HTMLElement>('.player-dot');
    const enemyDot = document.querySelector<HTMLElement>('.danger-dot');
    if (playerDot) {
      playerDot.style.left = `${12 + 76 * this.player.x / this.scale.width}%`;
      playerDot.style.top = '68%';
    }
    if (enemyDot && this.enemy) {
      enemyDot.style.left = `${12 + 76 * this.enemy.x / this.scale.width}%`;
      enemyDot.style.right = 'auto';
      enemyDot.style.top = '68%';
      enemyDot.style.visibility = this.combat.enemyHp > 0 ? 'visible' : 'hidden';
    }
    this.drawMinimap();
    const bosses=onlineSession.current.snapshot?.enemies.filter(e=>e.boss&&e.hp>0&&(e.mapId??'village')===this.mapId)??[];
    const bossNearby=this.onlineEnabled?bosses.some(e=>Math.abs(e.x-this.motion.x)<700):this.activeEnemyDef.boss&&this.combat.enemyHp>0&&!!this.enemy&&Math.abs(this.enemy.x/this.worldScale-this.motion.x)<700;
    worldMusic.setMood(bossNearby?'boss':'haunting');
    document.body.dataset.warpTalismans=String(this.onlineEnabled?(onlineSession.current.snapshot?.players.find(p=>p.id===onlineSession.current.playerId)?.inventory?.['warp-talisman']??0):this.localTalismans);
    document.body.dataset.enemyPresent=this.enemy?.active && this.enemy.visible?'1':'0';
    document.body.dataset.mapId=this.mapId;
    document.body.dataset.warpPending=String(this.warpPending);
    document.body.dataset.worldX=this.motion.x.toFixed(2);
    document.body.dataset.enemyWorldX=this.enemyMotion.x.toFixed(2);
    document.body.dataset.enemyAltitude=this.enemyMotion.height.toFixed(2);
    document.body.dataset.enemyDepth=this.enemyMotion.depth.toFixed(2);
    document.body.dataset.cameraX=this.cameras.main.scrollX.toFixed(2);
    document.body.dataset.outfit=this.character.equipment.outfit;
    document.body.dataset.profession=this.character.profession;
    document.body.dataset.shortcuts=JSON.stringify(this.character.shortcuts);
    document.body.dataset.shortcutKeys=JSON.stringify(this.character.shortcutKeys);
    document.body.dataset.playerFeet=String(this.player.y);
    document.body.dataset.statAtk=String(characterStats(this.character,this.combat.level).atk);
    document.body.dataset.playerX = Math.round(this.player.x-this.cameras.main.scrollX).toString();
    document.body.dataset.playerDepth = this.motion.depth.toFixed(2);
    document.body.dataset.playerAltitude = this.motion.height.toFixed(2);
    document.body.dataset.motionMode = this.motion.mode;
    document.body.dataset.playerGrounded = this.motion.grounded ? '1' : '0';
    document.body.dataset.playerVx = this.motion.vx.toFixed(2);
    document.body.dataset.armAngle = (this.hunterRig?.armAngle ?? 0).toFixed(2);
    document.body.dataset.kneeAngle = (this.hunterRig?.kneeAngle ?? 0).toFixed(2);
    document.body.dataset.playerHp = this.combat.playerHp.toString();
    document.body.dataset.enemyStunned = this.time.now < this.combat.enemyStunnedUntil ? '1' : '0';
    document.body.dataset.enemyHp = this.combat.enemyHp.toString();
    document.body.dataset.enemyName = this.activeEnemyDef.nameTh;
    document.body.dataset.enemyTelegraphing = this.enemyTelegraphing ? '1' : '0';
    document.body.dataset.combo = this.combat.combo.toString();
    const progression = this.progressionLoop.progression;
    const world = this.progressionLoop.world;
    document.body.dataset.level = progression.level.toString();
    document.body.dataset.exp = progression.exp.toString();
    document.body.dataset.bie = progression.bie.toString();
    document.body.dataset.spiritEssence = progression.spiritEssence.toString();
    document.body.dataset.haunting = world.haunting.toString();
    document.body.dataset.bossActive = world.bossActive ? '1' : '0';
    document.body.dataset.loopCycle = world.cycle.toString();
    document.body.dataset.lastLoot = this.lastLootText;
    document.body.dataset.inventoryKrasueAppendix = String(this.huntInventory.stacks['krasue-appendix'] ?? 0);
    document.body.dataset.inventoryKrahangFragment = String(this.huntInventory.stacks['krahang-winnowing-fragment'] ?? 0);
  }
}
